/**
 * ComponentLineRow — the per-line card shown inside every section of the
 * Components form (Sheet / Tile / Ridge / Valley / Barge / Flashing /
 * Fastening / Spouting / Cleaning / Travel / …).
 *
 * Iter220 — extracted verbatim from `AppraisalComponentsTab.js`
 * (was the inline `ComponentLineRow` defined at the bottom of that file)
 * so the Repairs builders (Iron / Concrete / Tin-Tile) can adopt the
 * same row UX from Iter221 onwards. Zero behaviour change vs. the
 * original; all data-testids and class names preserved.
 *
 * The full row is composed of:
 *   • F-chip / ref id badge + SourceBadge (from-Diagram / Edited / …)
 *   • Type column: per-row material narrow + Type cascade dropdown + ↓ALL pills
 *   • Profile / Flashing-Type column (Profile dropdown OR Flashing-category
 *     picker when component_type === 'Flashing') + Roof-profile badge
 *   • Cover + Face Width (Sheet section only) with live "→ N sheets" calc
 *   • QTY / LENGTH / TOTAL trio
 *   • Customer / Cost split with +profit delta
 *   • Trash remove button
 *   • Conditional sub-strips:
 *       – Spouting Ends (mitre 0/1/2 + S-End L/R + outlet size + qty + new-length)
 *       – Fastening details (m² source picker + screw rate + order screws)
 *       – Flashing template strip (template picker + per-fold inputs + Σ girth + rate hint)
 *   • Ridge / Barge cap panel (uses /dropdowns/{ridge|barge}_cap_types)
 *   • Expandable Rate-card · supplier · description panel
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import RateDetailEditButton from '@/components/RateDetailEditButton';
import { FasteningPositionPicker } from '@/components/AppraisalComponents/FasteningPositionPicker';
// Iter316 · P0-1 · shared flashing-rate matcher (Valley/Barge cascade
// used a row-local copy previously; extracted so the new parent-level
// Flashing cascade hook stays DRY with this row-local usage).
import { resolveRateByGirthFolds as _resolveRateByGirthFolds } from '@/components/AppraisalComponents/hooks/flashingRateResolver';
import FlashingTemplatePickerInline from '@/components/shared/ComponentRow/FlashingTemplatePickerInline';
import DektiteLinkInline from '@/components/shared/ComponentRow/DektiteLinkInline';
import DektiteCascadeInline from '@/components/shared/ComponentRow/DektiteCascadeInline';
import BacktrayLinkInline from '@/components/shared/ComponentRow/BacktrayLinkInline';
import SourceBadge from '@/components/shared/ComponentRow/SourceBadge';
import CapPanel from '@/components/shared/ComponentRow/CapPanel';
import { FoldCrossSectionPreview } from '@/components/annotator/FoldEditor';
import {
  rateHasMaterial,
  fmt,
  inferMaterialFromContext,
  isAutoBracketLine,
  isGutterLengthLine,
  lineTotals,
  RATE_MODAL_PRESET_BY_SECTION,
  snapRun,
  UNIT_BASED_TYPES,
  withMitreTag,
} from '@/components/shared/ComponentRow/helpers';

/**
 * Tiny ↓ALL pill — one-click cascade of the current row's value to every
 * sibling row in the same component_type group. Alt/Shift-click overrides
 * existing values; plain click only fills blanks. Toast feedback explains
 * what happened so the operator never wonders if anything changed.
 */
function ApplyToAllBtn({ field, value, label, ref_id, siblingCount, sectionKey, onApplyToAll }) {
  if (!onApplyToAll || siblingCount <= 1 || !value) return null;
  return (
    <button
      type="button"
      data-testid={`apply-to-all-${field}-${ref_id}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const force = e.altKey || e.shiftKey;
        const result = onApplyToAll(field, value, { force }) || { touched: 0, total: 0 };
        const { touched = 0, total = 0 } = result;
        if (touched > 0) {
          const extra = field === 'type_name' ? ' (with rates)' : '';
          toast.success(`Copied ${label} → ${touched} row${touched === 1 ? '' : 's'}${force ? ' (overwritten)' : ''}${extra}`);
        } else if (total === 0) {
          toast.info(`No other ${sectionKey} rows to copy to.`);
        } else if (force) {
          toast.info(`${label} on every row already matches "${String(value).slice(0, 40)}".`);
        } else {
          toast.info(`All ${total} other ${sectionKey} row${total === 1 ? '' : 's'} already have a ${label.toLowerCase()} set — Alt-click ↓ ALL to overwrite.`);
        }
      }}
      title={`Copy ${label} to all ${siblingCount - 1} other ${sectionKey} rows (Alt/Shift-click to overwrite). Type cascades also stamp rate $ and supplier.`}
      className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
    >
      ↓ all
    </button>
  );
}

export default function ComponentLineRow({
  api, line, rates, profiles, sheetProfileMap, colour, ref_id, isAdmin,
  materialList, sectionKey, siblingCount = 0,
  roofTypeCtx = '', roofBrandCtx = '', roofProfileCtx = '', sheetSectionProfile = '',
  sectionShowAll = false, mitreExtraM = 0, roofM2 = 0, facePinLengths = [],
  flashingTemplates = [],
  flashingLines = [],
  sheetLines = [],
  valleyProfileOptions = [],
  bargeProfileOptions = [],
  primaryMaterialCtx = '',
  quoteTypeCtx = '',
  appraisalIdCtx = '',
  roofSubtypeCtx = '',
  tileProfileOverrideCtx = '',
  onUpdate, onPickType, onRemove, onApplyToAll,
  onRequestRatesRefresh,
  onLinkDektite, onUnlinkDektite, onChangeDektite,
  onLinkBacktray, onUnlinkBacktray, onChangeBacktray,
}) {
  const [editingRates, setEditingRates] = useState(line.override || false);
  // Iter147 — per-row "See all" override. Bypass = both material-narrow AND
  // cascade-link narrow are skipped, exposing the full `rates` list.
  const [rowShowAll, setRowShowAll] = useState(false);
  // Iter224 — Snap-up Undo. When a folds edit auto-applies a new Type
  // (e.g. Galv 700/3F → Galv 800/3F), we stash the previous Type +
  // rate fields so the operator can revert with one click.
  const [snapUndo, setSnapUndo] = useState(null);   // { from_type_name, to_type_name, prev_rates }
  // Iter249 — Track when we're minting a synthetic Valley rate row so
  // the UI can show a "⏳ Composing custom rate…" chip + disable
  // further picks until the round-trip finishes.
  const [synthMinting, setSynthMinting] = useState(false);
  // Iter249 — Valley-only coil width ceiling. Any girth above this is
  // physically impossible (one-piece flashing coils stop at 1200mm).
  const VALLEY_COIL_MAX_MM = 1200;
  const bypassCascade = sectionShowAll || rowShowAll;

  // Iter248 — Valley Type cascade resolver. Given (material, girth, bends)
  // finds the matching catalogue rate row (e.g. "New Galv Valley (400G/5F)")
  // so picking a Profile/Template can set `type_name` to the rate name
  // (not the template name). Returns null when no match; caller can fall
  // back to leaving type_name blank + showing a warning.
  //
  // Iter316 · P0-1 · logic extracted to `flashingRateResolver.js` so the
  // parent-level `useFlashingRateCascade` hook shares the same matcher.
  // Wrapper passes the row-scope `rates` prop through unchanged.
  const resolveRateByGirthFolds = (material, girth, bends) =>
    _resolveRateByGirthFolds(material, girth, bends, rates);

  // Iter249 — When no in-catalogue Valley rate fits (girth > 400mm on
  // 5F, or > 300mm on 3F, or any non-standard bend count), ask the
  // backend to mint a synthetic Valley rate by borrowing material
  // cost from the Flashing catalogue. Idempotent: re-requests with
  // the same (material, girth, bends) return the existing row.
  // Returns the rate row (or null on failure / above coil ceiling).
  //
  // Iter250 — Extended to also handle Barge rows. When `sectionKey`
  // is 'Barge' we POST to /component-rates/barge-synthetic instead,
  // passing the `sheetProfile` (Corrugate / Trimrib / Ribline /
  // Butynol) so the new row is named consistently with the catalogue
  // ("Corru Barge Galv 620G/5F" etc.).
  const mintSyntheticValleyRate = async (material, girth, bends, opts = {}) => {
    if (!material || !girth || !bends) return null;
    if (girth > VALLEY_COIL_MAX_MM) return null;
    const isBarge = sectionKey === 'Barge';
    if (isBarge && !opts.sheetProfile) return null;
    try {
      setSynthMinting(true);
      const url = isBarge
        ? '/component-rates/barge-synthetic'
        : '/component-rates/valley-synthetic';
      const body = isBarge
        ? { material, girth_mm: girth, bends, sheet_profile: opts.sheetProfile, family: opts.family || 'iron' }
        : { material, girth_mm: girth, bends };
      const { data } = await api.post(url, body);
      if (typeof onRequestRatesRefresh === 'function') {
        // Tell the parent to refetch the rate list so subsequent rows /
        // pickers see the newly-minted row in the dropdown.
        onRequestRatesRefresh(isBarge ? 'Barge' : 'Valley');
      }
      return data;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Synthetic rate mint failed', e?.response?.data || e?.message);
      return null;
    } finally {
      setSynthMinting(false);
    }
  };

  // Iter250 — Derive the canonical Barge sheet-profile token used to
  // name a synthetic Barge rate. Order of preference: template's
  // sheet_profiles array → template name parse → row's current
  // sheet_profile field → roof's profile context. Returns '' when
  // nothing usable is found.
  const inferBargeSheetProfile = (tpl, profileOpt) => {
    const fromTplProfiles = (tpl?.sheet_profiles || [])[0]
                         || (profileOpt?.sheet_profiles || [])[0];
    if (fromTplProfiles) return fromTplProfiles;
    const tplName = String(tpl?.name || profileOpt?.template_name || '').toLowerCase();
    if (tplName.includes('corru'))   return 'Corrugate';
    if (tplName.includes('trib'))    return 'Trimrib';
    if (tplName.includes('ribline')) return 'Ribline';
    if (tplName.includes('butynol')) return 'Butynol';
    if (line.sheet_profile) return line.sheet_profile;
    if (roofProfileCtx)      return roofProfileCtx;
    return '';
  };

  // Iter251 — Shared cascade-pick handler used by BOTH the Profile
  // (signature) and Profile Ref (template name) dropdowns. Picking
  // either fires the same Type cascade — material confirm, snap rate,
  // synthetic mint when out of catalogue, etc.
  const applyProfileOptionPick = async (opt) => {
    if (!opt) { onUpdate({ profile: '', profile_ref: '' }); return; }
    const newFolds = (opt.folds || []).map(f => ({ ...f }));
    const newGirth = Number(opt.girth_mm) || newFolds.reduce((a, f) => a + (Number(f.length_mm) || 0), 0);
    const tplFoldMatch = String(opt.template_name || '').toLowerCase().match(/(\d+)\s*fold/);
    const newBends = tplFoldMatch ? parseInt(tplFoldMatch[1], 10) : Math.max(0, newFolds.length - 1);

    let effMaterial = line.material || '';
    if (!effMaterial && primaryMaterialCtx) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `This ${sectionKey} row has no Material set.\n\n` +
        `Default to "${primaryMaterialCtx}" (the appraisal's primary material) so the Type can auto-resolve?`
      );
      if (ok) effMaterial = primaryMaterialCtx;
    }

    let matchedRate = resolveRateByGirthFolds(effMaterial, newGirth, newBends);
    if (!matchedRate && effMaterial && newGirth > 0 && newBends > 0 && newGirth <= VALLEY_COIL_MAX_MM) {
      const sheetProfile = usesBargeCascade ? inferBargeSheetProfile(null, opt) : null;
      matchedRate = await mintSyntheticValleyRate(effMaterial, newGirth, newBends, { sheetProfile });
    }

    const patch = {
      profile: opt.signature,
      profile_ref: opt.template_name || '',
      flashing_template_id: opt.template_id,
      linked_flashing_template_id: opt.template_id,
      linked_flashing_name: opt.template_name,
      folds: newFolds,
      folds_qty: newBends,
      girth_mm: newGirth,
      start_angle_deg: opt.start_angle_deg || 0,
    };
    if (effMaterial && effMaterial !== line.material) patch.material = effMaterial;
    if (usesBargeCascade) {
      const sp = inferBargeSheetProfile(null, opt);
      if (sp) patch.sheet_profile = sp;
    }
    if (matchedRate) {
      patch.type_name      = matchedRate.type_name;
      patch.buy_cost       = Number(matchedRate.buy_cost)       || 0;
      patch.materials_rate = Number(matchedRate.materials_rate) || 0;
      patch.labour_rate    = Number(matchedRate.labour_rate)    || 0;
      patch.contractor_rate= Number(matchedRate.contractor_rate)|| 0;
      patch.override       = false;
    } else {
      patch.type_name = '';
    }
    onUpdate(patch);
  };

  // Iter251 — Distinct Profile Ref options keyed by template_id so the
  // same signature with multiple template names (e.g. TRib + Ribline
  // both at 25/240/80/15/10) surfaces as TWO selectable rows. Moved to
  // sit AFTER `profileOptionsForSection` to avoid TDZ.
  // Iter250 — Material-driven cascade unlock. Iron-family materials
  // (Galv/Zinc/Maxx/Endura/ZinaCore/MagnaFlow/ZR8/Coloursteel/ZCore)
  // unlock the Valley-style cascade UI on Barge rows. Concrete/Clay/Tile
  // materials hide the Profile dropdown + cross-section preview.
  const IRON_MATERIAL_RX = /(galv|zinc|maxx|endura|zinacore|magnaflow|zr8|coloursteel|zcore|butynol|colour\s*steel)/i;
  const isIronFamily = (mat) => !!(mat && IRON_MATERIAL_RX.test(mat));
  // Iter250 — Sections that get the full Profile+sync+preview+synth UI.
  const usesValleyCascade = sectionKey === 'Valley';
  const usesBargeCascade  = sectionKey === 'Barge' && isIronFamily(line.material || primaryMaterialCtx);
  const usesFlashingCascade = usesValleyCascade || usesBargeCascade;
  // The relevant profile-options list for the row's section.
  const profileOptionsForSection = usesValleyCascade
    ? (valleyProfileOptions || [])
    : (usesBargeCascade ? (bargeProfileOptions || []) : []);
  // All templates that share the row's current Profile signature.
  const profileRefForCurrentSignature = (line.profile || '')
    ? (profileOptionsForSection || []).filter(o => o.signature === line.profile)
    : [];
  // Whether the operator's current `profile_ref` matches one of the
  // templates compatible with the current signature. Used to flag a
  // mismatch when both are set but inconsistent.
  const profileRefConsistent = (() => {
    if (!line.profile || !line.profile_ref) return true; // either blank → no conflict
    return profileRefForCurrentSignature.some(o => o.template_name === line.profile_ref);
  })();

  // Iter251 — Starter/Finish role toggle. Barge default = "starter"
  // (pre-ordered at maximum width). Flashing default = "finish"
  // (custom-measured after roofing is fitted). Persisted as
  // `barge_role` / `flashing_role` on the line.
  const roleField = sectionKey === 'Barge' ? 'barge_role'
                  : sectionKey === 'Flashing' ? 'flashing_role'
                  : null;
  const roleDefault = sectionKey === 'Barge' ? 'starter'
                    : sectionKey === 'Flashing' ? 'finish'
                    : '';
  const currentRole = roleField ? (line[roleField] || roleDefault) : '';

  // Iter224 — Row-local material auto-cascade for Flashing rows. The
  // parent AppraisalComponentsTab.js runs an autofill effect on first
  // hydration but it's gated by `autoFilledOnce` and depends on
  // `quoteType` arriving in time. On A5004 freshly-pushed Flashing
  // pin-stub rows were sometimes left with blank Material because the
  // one-shot autofill fired before quote_type_ref hydrated. This effect
  // fills the gap row-locally — fires only for Flashing rows whose
  // current material is either blank OR a value the section's catalogue
  // doesn't recognise (defensive against upstream seeders that
  // accidentally write garbage material tokens like 'Flashing').
  useEffect(() => {
    if (line.component_type !== 'Flashing') return;
    if (!quoteTypeCtx && !roofTypeCtx && !roofBrandCtx) return;
    if (!rates?.length) return;
    // Iter225 — Recognise the current material as "already set" only if
    // it appears in the section's actual rate catalogue. Otherwise treat
    // it as blank and let the cascade overwrite.
    const sec = sectionKey || 'Flashing';
    const currentLooksValid = !!line.material
      && rates.some(r => rateHasMaterial(sec, r, line.material));
    if (currentLooksValid) return;
    const inferred = inferMaterialFromContext({
      quoteType: quoteTypeCtx,
      roofType:  roofTypeCtx,
      roofBrand: roofBrandCtx,
    });
    if (!inferred) return;
    if (!rates.some(r => rateHasMaterial(sec, r, inferred))) return;
    if (line.material === inferred) return;   // already correct, no-op
    onUpdate?.({ material: inferred });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.component_type, line.material, quoteTypeCtx, roofTypeCtx, roofBrandCtx, rates, sectionKey]);
  const t = lineTotals(line, mitreExtraM);
  const hasType = !!line.type_name;
  const isSheet = line.component_type === 'Sheet';
  const isFlashing = line.component_type === 'Flashing';
  // iter314 R2.3 · ReCoat siblings carry a "N step" chip on the Profile
  // column (derived from paint_system.coats.length at diagram-push time).
  const isRecoat = line.component_type === 'ReCoat';
  const sheetCovers = isSheet && sheetProfileMap ? (sheetProfileMap[line.profile] || []) : [];

  // Bound version of <ApplyToAllBtn/> closing over the row's identity props.
  // Hoisted out of the row body (Iter220 lint fix) so React doesn't see a
  // fresh component identity on every render.
  const applyToAllProps = { ref_id, siblingCount, sectionKey, onApplyToAll };

  // Per-line material narrow-filter.
  const materialFiltered = bypassCascade
    ? rates
    : ((materialList && line.material)
        ? rates.filter(r => rateHasMaterial(sectionKey || line.component_type, r, line.material))
        : rates);

  // Iter134 — cascade filter by roof_type_links + profile_links.
  const ctxRoofType = String(roofTypeCtx || '').trim();
  const ctxBrand    = String(roofBrandCtx || '').trim();
  const ctxProfile  = String(roofProfileCtx || '').trim();
  const someLinkedToRoof = ctxRoofType && materialFiltered.some(r =>
    Array.isArray(r.roof_type_links) && r.roof_type_links.includes(ctxRoofType));
  const someLinkedToBrand = ctxBrand && materialFiltered.some(r =>
    Array.isArray(r.brand_links) && r.brand_links.includes(ctxBrand));
  const someLinkedToProfile = ctxProfile && materialFiltered.some(r =>
    Array.isArray(r.profile_links) && r.profile_links.includes(ctxProfile));
  const matchesRoofLink = (r) => {
    if (!ctxRoofType) return true;
    const arr = r.roof_type_links;
    if (someLinkedToRoof) return Array.isArray(arr) && arr.includes(ctxRoofType);
    return !Array.isArray(arr) || arr.length === 0 || arr.includes(ctxRoofType);
  };
  const matchesBrandLink = (r) => {
    if (!ctxBrand) return true;
    const arr = r.brand_links;
    if (someLinkedToBrand) return Array.isArray(arr) && arr.includes(ctxBrand);
    return !Array.isArray(arr) || arr.length === 0 || arr.includes(ctxBrand);
  };
  const matchesProfileLink = (r) => {
    if (!ctxProfile) return true;
    const arr = r.profile_links;
    if (someLinkedToProfile) return Array.isArray(arr) && arr.includes(ctxProfile);
    return !Array.isArray(arr) || arr.length === 0 || arr.includes(ctxProfile);
  };
  const cascadeFiltered = bypassCascade
    ? materialFiltered
    : materialFiltered.filter(r => matchesRoofLink(r) && matchesBrandLink(r) && matchesProfileLink(r));
  const someHaveLinks = materialFiltered.some(r =>
    (Array.isArray(r.roof_type_links) && r.roof_type_links.length) ||
    (Array.isArray(r.brand_links)     && r.brand_links.length)     ||
    (Array.isArray(r.profile_links)   && r.profile_links.length));
  const cascadeStripped = !bypassCascade && someHaveLinks && cascadeFiltered.length === 0;
  const baseVisible = cascadeStripped ? materialFiltered : cascadeFiltered;
  const sheetProfileForFilter = isSheet ? String(sheetSectionProfile || line.profile || '').trim() : '';
  const profileNameFiltered = (sheetProfileForFilter && !bypassCascade)
    ? baseVisible.filter(r => String(r.type_name || '').toLowerCase().includes(sheetProfileForFilter.toLowerCase()))
    : baseVisible;
  const baseVisibleFinal = (sheetProfileForFilter && !bypassCascade && profileNameFiltered.length === 0)
    ? baseVisible
    : profileNameFiltered;
  const activeRateRow = (rates || []).find(r => r.type_name === line.type_name);
  // Iter249 — Synthetic Valley rate fallback. When the line carries a
  // type_name that the catalogue doesn't know about yet (e.g. freshly
  // minted "New Galv Valley (620G/5F)" before the parent's rates
  // cache has refreshed), still surface it in the Type dropdown so
  // the operator sees what's selected.
  const syntheticRateRow = (!activeRateRow && line.type_name && (sectionKey === 'Valley' || sectionKey === 'Barge'))
    ? {
        _id: `synth:${line.type_name}`,
        type_name: line.type_name,
        buy_cost: line.buy_cost,
        materials_rate: line.materials_rate,
        labour_rate: line.labour_rate,
        contractor_rate: line.contractor_rate,
        synthetic: true,
      }
    : null;
  const effectiveActive = activeRateRow || syntheticRateRow;
  const visibleRates = (line.type_name && effectiveActive && !baseVisibleFinal.some(r => r.type_name === line.type_name))
    ? [effectiveActive, ...baseVisibleFinal]
    : baseVisibleFinal;
  const cascadeWarning = cascadeStripped
    ? `⚠ No rate rows linked to "${[ctxRoofType, ctxBrand, ctxProfile].filter(Boolean).join(' / ')}". Showing all — link some rows in /component-rates to enable cascade.`
    : null;

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-3 pl-12 grid grid-cols-12 gap-2 items-start relative" data-testid="comp-line">
      {/* Per-component sequential Ref ID (e.g. Cl1, Tr2, Sh3) */}
      <span data-testid="comp-line-ref" className="absolute left-2 top-3 inline-flex items-center justify-center min-w-[34px] px-1.5 py-1 rounded-md text-[10px] font-bold tracking-wide font-mono-num text-white shadow-sm" style={{ background: colour }}>
        {ref_id}
      </span>
      {/* Source provenance badge */}
      <SourceBadge line={line} />

      {/* Type selector */}
      <div className={`col-span-12 ${isSheet ? 'md:col-span-2' : 'md:col-span-3'}`}>
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 flex items-center">
          Type <ApplyToAllBtn field="type_name" value={line.type_name} label="Type" {...applyToAllProps} />
          {(() => {
            const activeRate = (rates || []).find(r => r.type_name === line.type_name);
            if (!activeRate) return null;
            const preset = RATE_MODAL_PRESET_BY_SECTION[sectionKey] || 'sheet';
            return (
              <RateDetailEditButton
                component={preset}
                rate={{ ...activeRate, id: activeRate._id || activeRate.id }}
                testid={`comp-line-rate-detail-${ref_id}`}
                pinContext={{
                  label: ref_id,
                  hideMaster: true,
                  detailOverride: line.override
                    ? {
                        buy_cost: line.buy_cost,
                        materials_rate: line.materials_rate,
                        labour_rate: line.labour_rate,
                        contractor_rate: line.contractor_rate,
                      }
                    : null,
                  onSaveOverride: async (body) => {
                    const patch = { override: true };
                    ['buy_cost', 'materials_rate', 'labour_rate', 'contractor_rate'].forEach(k => {
                      if (body[k] !== undefined) patch[k] = Number(body[k]) || 0;
                    });
                    ['supplier', 'supplier_code', 'materials_desc', 'labour_details'].forEach(k => {
                      if (body[k] !== undefined) patch[k] = body[k];
                    });
                    onUpdate(patch);
                  },
                }}
              />
            );
          })()}
          <button
            type="button"
            data-testid={`row-show-all-${ref_id}`}
            onClick={() => setRowShowAll(v => !v)}
            disabled={sectionShowAll}
            title={sectionShowAll
              ? 'Section "Show all" is active — every row in this section bypasses the cascade.'
              : rowShowAll
                ? `Cascade bypassed on ${ref_id} — full catalogue (${rates.length}). Click to re-engage.`
                : `Bypass cascade on ${ref_id} only — show all ${rates.length} rates in this section.`}
            className={`ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border transition-colors ${
              sectionShowAll
                ? 'bg-amber-50 text-amber-700 border-amber-200 cursor-not-allowed'
                : bypassCascade
                  ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                  : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300'
            }`}
          >
            {bypassCascade ? `▴ all ${rates.length}` : `▾ all`}
          </button>
        </label>
        {materialList && materialList.length > 0 && (
          <div className="flex items-center gap-1 mb-1">
            <select
              data-testid="comp-line-material"
              value={line.material || ''}
              onChange={e => {
                // Jun 2026 — a Type that doesn't belong to the new material is cleared so the cascade re-resolves it.
                const m = e.target.value;
                const cur = rates.find(r => r.type_name === line.type_name);
                const stale = m && cur && !line.override && !rateHasMaterial(line.component_type, cur, m);
                onUpdate(stale
                  ? { material: m, type_name: '', buy_cost: 0, materials_rate: 0, labour_rate: 0, contractor_rate: 0 }
                  : { material: m });
              }}
              className="flex-1 text-[11px] font-semibold px-1.5 py-0.5 border border-slate-200 rounded bg-slate-50 text-slate-700"
              title="Filter the Type dropdown for this row"
            >
              <option value="">All materials</option>
              {materialList.map(b => <option key={b.key} value={b.key}>{b.key} ({b.count})</option>)}
            </select>
            <ApplyToAllBtn field="material" value={line.material} label="Material" {...applyToAllProps} />
          </div>
        )}
        <select data-testid="comp-line-type" value={line.type_name} onChange={e => onPickType(e.target.value)} className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-white" style={{ borderColor: hasType ? colour + '40' : undefined }}>
          <option value="">— Select —</option>
          {visibleRates.map(r => <option key={r._id} value={r.type_name}>{r.type_name}</option>)}
        </select>
        {cascadeWarning && (
          <div data-testid="cascade-warning" className="mt-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{cascadeWarning}</div>
        )}
        {/* Iter251 — Materials Order chip (read-only). Shows the linked
            Flashing rate that sourced/would source the row's materials.
            Computed by the backend on /component-rates fetch as
            `linked_flashing_order`. Only renders when a rate is picked
            AND a linked order is derivable. */}
        {effectiveActive && (effectiveActive.linked_flashing_order || effectiveActive.source_type_name) && (
          <div
            data-testid={`comp-line-materials-order-${ref_id}`}
            title="Flashing materials order linked to this rate — used by the supplier order PDF."
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono-num rounded px-1.5 py-0.5 border bg-slate-50 text-slate-600 border-slate-200"
          >
            <span aria-hidden="true">📦</span>
            <span className="text-slate-400">Order:</span>
            <strong className="text-slate-700">{effectiveActive.linked_flashing_order || effectiveActive.source_type_name}</strong>
          </div>
        )}
        {/* Iter251 — Starter/Finish role pill toggle for Barge + Flashing.
            Quote always uses starter-width pricing; finish rows just
            mark themselves for measure-up after roof install. The
            actual narrower finish width persists separately as
            `final_measured_girth_mm` and doesn't change pricing. */}
        {roleField && (
          <div
            data-testid={`comp-line-role-toggle-${ref_id}`}
            role="group"
            aria-label={`${sectionKey} role`}
            className="mt-1 inline-flex rounded-full overflow-hidden text-[10px] font-bold uppercase tracking-wider border border-slate-200"
          >
            {['starter', 'finish'].map(opt => (
              <button
                key={opt}
                type="button"
                data-testid={`comp-line-role-${opt}-${ref_id}`}
                onClick={() => onUpdate({ [roleField]: opt })}
                className={`px-2 py-0.5 transition-colors ${
                  currentRole === opt
                    ? (opt === 'starter'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-amber-500 text-white')
                    : 'bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Profile (per-component dropdown) — for Flashing rows this slot
          becomes the "Flashing Type" category picker. */}
      <div className="col-span-6 md:col-span-2">
        {isFlashing ? (
          <>
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 flex items-center justify-between">
              <span>Flashing Type</span>
              {roofProfileCtx && (
                <span data-testid={`comp-line-roof-profile-badge-${ref_id}`} title={`Roof Profile from Roof Details: ${roofProfileCtx}`} className="ml-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                  Roof: {roofProfileCtx}
                </span>
              )}
            </label>
            {(() => {
              const FLASHING_CATEGORIES = ['Apron', 'Back Tray', 'Barge', 'Box Gutter', 'Change Of Pitch', 'Gutter Flashing', 'Parapet', 'Skirt', 'Valley'];
              const libCats = Array.from(new Set((flashingTemplates || []).map(t => t.category).filter(Boolean)));
              const cats = libCats.length ? libCats : FLASHING_CATEGORIES;
              return (
                <select
                  data-testid="comp-line-flashing-type"
                  value={line.flashing_type || ''}
                  onChange={(e) => onUpdate({ flashing_type: e.target.value, flashing_template_id: '', girth_mm: 0, folds_qty: 0 })}
                  className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-white"
                  style={{ borderColor: (line.flashing_type ? colour + '40' : undefined) }}
                >
                  <option value="">— Select —</option>
                  {cats.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              );
            })()}
          </>
        ) : (
          <>
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 flex items-center">
              Profile <ApplyToAllBtn field="profile" value={line.profile} label="Profile" {...applyToAllProps} />
            </label>
            {/* Iter247/250 — Valley + Barge rows: Profile dropdown is
                driven by the catalogue's known template signatures.
                Picking a signature applies the matching template in
                ONE click (folds, start_angle, type_name, linked_*).
                Barge rows only get the cascade when the row's
                material is iron-family. */}
            {usesFlashingCascade && Array.isArray(profileOptionsForSection) && profileOptionsForSection.length > 0 ? (
              // Iter251 — Profile + Profile Ref split: two side-by-side
              // dropdowns. Picking either fires the same cascade via
              // applyProfileOptionPick. Operator can identify by
              // dimension signature OR by template name.
              <div className="flex gap-1 items-stretch">
                <select
                  data-testid={`comp-line-profile-cascade-${ref_id}`}
                  value={line.profile || ''}
                  onChange={async e => {
                    const sig = e.target.value;
                    if (!sig) { applyProfileOptionPick(null); return; }
                    // Prefer the template whose name matches the row's
                    // current profile_ref (so a Ribline-then-resignature
                    // pick keeps Ribline), else fall back to the first.
                    const matches = profileOptionsForSection.filter(o => o.signature === sig);
                    const preferred = matches.find(o => o.template_name === line.profile_ref) || matches[0];
                    if (!preferred) { onUpdate({ profile: sig, profile_ref: '' }); return; }
                    await applyProfileOptionPick(preferred);
                  }}
                  className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-white"
                  title="Pick by fold dimensions"
                >
                  <option value="">— Profile —</option>
                  {/* Dedup signatures so the Profile dropdown only shows each shape once. */}
                  {Array.from(new Map(profileOptionsForSection.map(o => [o.signature, o])).values()).map(o => (
                    <option key={o.signature} value={o.signature}>
                      {o.signature}{o.provisional_dimensions ? ' ⚠prov' : ''}
                    </option>
                  ))}
                  {line.profile && !profileOptionsForSection.some(o => o.signature === line.profile) && (
                    <option value={line.profile}>{line.profile} (custom)</option>
                  )}
                </select>
                <select
                  data-testid={`comp-line-profile-ref-${ref_id}`}
                  value={line.profile_ref || ''}
                  onChange={async e => {
                    const refName = e.target.value;
                    if (!refName) { onUpdate({ profile_ref: '' }); return; }
                    // Prefer template matching the row's current signature
                    // when the operator picks a name that matches multiple
                    // signatures; else pick the first option by that name.
                    const matches = profileOptionsForSection.filter(o => o.template_name === refName);
                    const preferred = matches.find(o => o.signature === line.profile) || matches[0];
                    if (!preferred) { onUpdate({ profile_ref: refName }); return; }
                    await applyProfileOptionPick(preferred);
                  }}
                  className="flex-1 min-w-0 text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-white"
                  title="Pick by template name"
                >
                  <option value="">— Profile Ref —</option>
                  {/* Dedup template names; show all distinct refs across signatures */}
                  {Array.from(new Set(profileOptionsForSection.map(o => o.template_name))).filter(Boolean).map(name => {
                    const sample = profileOptionsForSection.find(o => o.template_name === name);
                    return (
                      <option key={name} value={name}>
                        {name}{sample?.provisional_dimensions ? ' ⚠prov' : ''}
                      </option>
                    );
                  })}
                  {line.profile_ref && !profileOptionsForSection.some(o => o.template_name === line.profile_ref) && (
                    <option value={line.profile_ref}>{line.profile_ref} (custom)</option>
                  )}
                </select>
              </div>
            ) : profiles.length > 0 ? (
              <select data-testid="comp-line-profile" value={line.profile || ''} onChange={e => {
                const v = e.target.value;
                const patch = { profile: v };
                if (isSheet && sheetProfileMap) {
                  let covers = sheetProfileMap[v] || [];
                  if (!covers.length && Array.isArray(rates)) {
                    const seen = new Set();
                    rates.forEach(r => {
                      const tn = String(r?.type_name || '');
                      if (!tn.toLowerCase().includes(v.toLowerCase())) return;
                      const mm = tn.match(/(\d{3,4})\s*mm/i);
                      if (mm) seen.add(parseFloat((parseInt(mm[1], 10) / 1000).toFixed(3)));
                    });
                    covers = Array.from(seen).sort((a, b) => a - b);
                  }
                  if (covers.length === 1) {
                    patch.cover_width = covers[0];
                  } else if (covers.length > 1) {
                    patch.cover_width = covers.includes(line.cover_width)
                      ? line.cover_width
                      : covers[covers.length - 1];
                  }
                  // iter313f Bug B — auto-recalc [Qty] when profile pick
                  // lands a cover_width AND the pin already brought a
                  // face_width (in line.face_length). Without this the
                  // "26.25 → 27 sheets" preview shows but the [Qty] input
                  // stays at 1 until the operator manually re-types
                  // Face Width or Cover.
                  if (patch.cover_width > 0 && Number(line.face_length) > 0) {
                    patch.quantity = Math.ceil(Number(line.face_length) / patch.cover_width);
                  }
                }
                onUpdate(patch);
              }} className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-white">
                <option value="">— Select —</option>
                {profiles.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            ) : isRecoat ? (
              // iter314 R2.3 · Read-only "N step" chip stamped by the
              // backend at diagram-push time (paint_system.coats.length).
              // Hides silently when `line.profile` is empty (subtype has
              // no default paint_system). Rendered as a chip — not an
              // input — because coats-count is a system-level property
              // of the roof, not a per-row operator toggle.
              line.profile ? (
                <span
                  data-testid={`comp-line-recoat-profile-chip-${ref_id}`}
                  title="Paint depth · derived from the appraisal's paint_system (coats.length)"
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200"
                >
                  {line.profile}
                </span>
              ) : (
                <span className="text-xs text-slate-400 italic">—</span>
              )
            ) : (
              <input data-testid="comp-line-profile" value={line.profile || ''} onChange={e => onUpdate({ profile: e.target.value })} placeholder="—" className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md" />
            )}
          </>
        )}
      </div>

      {/* Cover (Sheet only — auto-filled or selected from valid covers) */}
      {isSheet && (
        <div className="col-span-3 md:col-span-2">
          <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Cover (m)</label>
          {sheetCovers.length > 1 ? (
            <select data-testid="comp-line-cover" value={line.cover_width || ''} onChange={e => {
              const cov = parseFloat(e.target.value) || 0;
              const patch = { cover_width: cov };
              if (cov > 0 && Number(line.face_length) > 0) patch.quantity = Math.ceil(Number(line.face_length) / cov);
              onUpdate(patch);
            }} className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md bg-amber-50/40 font-mono-num">
              <option value="">—</option>
              {sheetCovers.map(c => <option key={c} value={c}>{c.toFixed(3)}</option>)}
            </select>
          ) : (
            <input data-testid="comp-line-cover" type="number" step="0.001" value={line.cover_width ?? 0} onChange={e => {
              const cov = parseFloat(e.target.value) || 0;
              const patch = { cover_width: cov };
              if (cov > 0 && Number(line.face_length) > 0) patch.quantity = Math.ceil(Number(line.face_length) / cov);
              onUpdate(patch);
            }} className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md font-mono-num bg-amber-50/40" title="Auto-fills from sheet profile (editable)" />
          )}
          <div className="mt-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Face Width (m)</label>
            <input data-testid="comp-line-face-length" type="number" step="0.01" value={line.face_length ?? 0} onChange={e => {
              const face = parseFloat(e.target.value) || 0;
              const patch = { face_length: face };
              if (face > 0 && Number(line.cover_width) > 0) patch.quantity = Math.ceil(face / Number(line.cover_width));
              onUpdate(patch);
            }} placeholder="0" className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md font-mono-num" title="Face width of the roof — divided by Cover and rounded up gives the sheet count. Maps from the Face pin's Face Width (m) value." />
            {Number(line.face_length) > 0 && Number(line.cover_width) > 0 && (
              <p className="mt-1 text-[10px] font-mono-num text-amber-800 bg-amber-50 ring-1 ring-amber-200 px-1.5 py-0.5 rounded text-center" data-testid="comp-line-sheets-calc">
                {(Number(line.face_length) / Number(line.cover_width)).toFixed(2)} → <span className="font-bold">{Math.ceil(Number(line.face_length) / Number(line.cover_width))} sheets</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Quantity */}
      <div className="col-span-3 md:col-span-1">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Qty</label>
        <input data-testid="comp-line-qty" type="number" step="1" value={line.quantity ?? 0} onChange={e => onUpdate({ quantity: parseFloat(e.target.value) || 0 })} className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md font-mono-num" />
      </div>

      {/* Length (or "Units" for unit-priced types) */}
      <div className="col-span-3 md:col-span-1">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{UNIT_BASED_TYPES.has(line.component_type) ? 'Units' : 'Length'}</label>
        <input data-testid="comp-line-length" type="number" step="1" value={line.length ?? 0} onChange={e => onUpdate({ length: parseFloat(e.target.value) || 0 })} className="w-full text-sm px-2 py-1.5 border border-[#E2E2DF] rounded-md font-mono-num" />
      </div>

      {/* Total (qty × length, falls back to qty when length=0; iter313f — Face-pin area override wins when set) */}
      <div className="col-span-3 md:col-span-1">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Total</label>
        {(() => {
          const override = Number(line.total_m2_override) || 0;
          const qty = Number(line.quantity) || 0;
          const len = Number(line.length) || 0;
          const total = override > 0 ? override : (len > 0 ? qty * len : qty);
          const totalTitle = override > 0
            ? `Face pin Area (m²) override — direct from pin data`
            : (len > 0 ? `${qty} × ${len}` : 'qty only (length = 0)');
          return (
            <div data-testid="comp-line-total" title={totalTitle} className="w-full text-sm font-bold px-2 py-1.5 border border-[#E2E2DF] rounded-md font-mono-num bg-slate-50 text-slate-800">
              {total.toFixed(2)}{override > 0 ? ' m²' : ''}
            </div>
          );
        })()}
        {isSheet && Number(line.cover_width) > 0 && Number(line.quantity) > 0 && !(Number(line.total_m2_override) > 0) && (
          <div data-testid="comp-line-sheet-metrics" className="text-[10px] text-slate-500 font-mono-num leading-tight mt-1">
            <div>
              Lineal <span className="text-slate-700 font-bold">
                {(Number(line.length) > 0 ? Number(line.quantity) * Number(line.length) : Number(line.quantity)).toFixed(2)} m
              </span>
            </div>
            <div>
              Roof <span className="text-slate-700 font-bold">
                {((Number(line.length) > 0 ? Number(line.quantity) * Number(line.length) : Number(line.quantity)) * Number(line.cover_width)).toFixed(2)} m²
              </span>
            </div>
          </div>
        )}
        {isSheet && Number(line.total_m2_override) > 0 && (
          <div data-testid="comp-line-sheet-metrics-override" className="text-[10px] font-mono-num leading-tight mt-1 text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded px-1.5 py-0.5" title="Face pin was set to Area-only mode — enter Length + Face Width on the pin to activate qty calculation">
            Area from pin · L+W not set
          </div>
        )}
      </div>

      {/* Customer / Cost */}
      <div className="col-span-12 md:col-span-2">
        <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Customer / Cost</label>
        <div data-testid={`comp-line-customer-cost-${ref_id}`} className="flex items-baseline gap-2 px-2 py-1.5 bg-slate-50 rounded-md border border-[#E2E2DF]">
          <span className="text-sm font-bold text-slate-900 font-mono-num">{fmt(t.customer_charge)}</span>
          <span className="text-[10px] text-slate-400 font-mono-num">cost {fmt(t.cost_to_us)}</span>
          <span className={`ml-auto text-[10px] font-bold font-mono-num ${t.materials_profit + t.labour_profit > 0 ? 'text-emerald-700' : 'text-rose-600'}`}>+{fmt(t.materials_profit + t.labour_profit)}</span>
        </div>
        {line.component_type === 'Sheet' && Number(line.cover_width) > 0 && Number(line.quantity) > 0 && Number(line.length) === 0 && (
          <button
            type="button"
            data-testid="comp-line-linear-apply"
            onClick={() => onUpdate({ length: parseFloat(Number(line.cover_width).toFixed(3)) })}
            title="Set Length = Cover so Total = Qty × Cover linear m"
            className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-200 px-1.5 py-0.5 rounded font-mono-num transition-colors"
          >
            Use cover ({Number(line.cover_width).toFixed(3)}m) as Length
          </button>
        )}
      </div>

      {/* Remove */}
      <div className="col-span-12 md:col-span-1 flex md:justify-end pt-4">
        <button type="button" onClick={onRemove} className="p-1.5 rounded text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors" title="Remove line"><Trash2 size={14} /></button>
      </div>

      {/* ── Iter286 · Phase 3 polycarbonate stock-length strip ─────────────
          Surfaces when the picked rate row is priced per lineal metre but
          sold ONLY in discrete supplier stock lengths (e.g. Corrugate
          Laserlite 2000 — ships at 1.8/2.1/2.4/…/7.2m sticks). Shows a
          Snap button (rounds the row's Length up to the smallest covering
          stick), a live "billed vs measured" pill, and a Colour picker
          when the rate row lists colour_options.
          The pricing math is applied server-side in
          `_compute_material_qty` — this strip is the operator's UI
          affordance so they SEE the ladder in play. */}
      {(() => {
        const stockMode = (activeRateRow?.stock_length_charge_mode || 'none').toLowerCase();
        const stockLadder = Array.isArray(activeRateRow?.stock_lengths) ? activeRateRow.stock_lengths : [];
        const colourOptions = Array.isArray(activeRateRow?.colour_options) ? activeRateRow.colour_options : [];
        const snapActive = stockMode === 'round_up_to_stock' && stockLadder.length > 0;
        if (!snapActive && colourOptions.length === 0) return null;
        const measured = Number(line.length) || 0;
        const snap = snapActive ? snapRun(measured, stockLadder) : null;
        const needsSnap = !!snap && measured > 0 && Math.abs(snap.billed - measured) > 1e-3;
        const wastePct = (snap && snap.billed > 0)
          ? ((snap.billed - measured) / snap.billed) * 100
          : 0;
        const totalSticks = (snap?.sticksUsed?.length || 0) * (Number(line.quantity) || 0);
        const perLineBilledM = (snap?.billed || 0) * (Number(line.quantity) || 0);
        return (
          <div
            className="col-span-12"
            data-testid={`comp-line-stocklength-strip-${ref_id}`}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-2 pr-2 py-1.5 rounded bg-sky-50/70 border border-sky-200">
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-sky-700 shrink-0">Stock-length</span>

              {snapActive && (
                <>
                  <span className="text-[10px] text-sky-900 font-mono-num">
                    Ladder: <span className="font-bold">{stockLadder.map(x => `${x}m`).join(' · ')}</span>
                  </span>
                  {measured > 0 && (
                    <span
                      data-testid={`comp-line-stock-snap-preview-${ref_id}`}
                      className={`text-[10px] font-mono-num px-1.5 py-0.5 rounded ${needsSnap ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-300' : 'bg-emerald-100 text-emerald-800'}`}
                      title={needsSnap ? 'Measured length is smaller than the nearest stock stick — supplier will ship the larger stick.' : 'Length already matches a supplier stock stick.'}
                    >
                      {measured.toFixed(2)}m → {snap.billed.toFixed(2)}m
                      {needsSnap && (
                        <span className="text-amber-700 ml-1">
                          ({wastePct.toFixed(1)}% waste)
                        </span>
                      )}
                    </span>
                  )}
                  {needsSnap && (
                    <button
                      type="button"
                      data-testid={`comp-line-stock-snap-btn-${ref_id}`}
                      onClick={() => onUpdate({
                        // Persist the raw measured value so the operator can
                        // see the pre-snap number in the future — the billed
                        // length becomes the new `length` (drives the order PDF).
                        measured_length_raw: measured,
                        length: snap.billed,
                      })}
                      className="text-[10px] font-bold px-2 py-0.5 rounded bg-sky-600 text-white hover:bg-sky-700 shadow-sm"
                      title={`Snap length up to nearest supplier stick (${snap.billed}m)`}
                    >
                      Snap ↑ to {snap.billed}m
                    </button>
                  )}
                  {measured > 0 && snap && snap.sticksUsed.length > 0 && Number(line.quantity) > 0 && (
                    <span
                      data-testid={`comp-line-stock-summary-${ref_id}`}
                      className="text-[10px] font-mono-num text-slate-600 ml-auto"
                      title="Total supplier sticks needed for this row"
                    >
                      {Number(line.quantity)} sheet{Number(line.quantity) === 1 ? '' : 's'} ·
                      <span className="ml-1 font-bold text-slate-800">
                        {totalSticks} × {snap.sticksUsed[0]}m sticks
                      </span>
                      <span className="text-slate-500 ml-1">({perLineBilledM.toFixed(1)}m billed)</span>
                    </span>
                  )}
                </>
              )}

              {colourOptions.length > 0 && (
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-700 flex items-center gap-1.5">
                  Colour
                  <select
                    data-testid={`comp-line-stock-colour-${ref_id}`}
                    value={line.chosen_colour || ''}
                    onChange={e => onUpdate({ chosen_colour: e.target.value })}
                    className="text-xs px-2 py-0.5 border border-sky-300 rounded bg-white font-mono-num"
                  >
                    <option value="">— pick —</option>
                    {colourOptions.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>
        );
      })()}

      {/* Spouting end-treatments & outlet droppers strip. */}
      {sectionKey === 'Spouting' && isGutterLengthLine(line) && (
        <div className="col-span-12" data-testid={`comp-line-spouting-ends-${ref_id}`}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 pr-2 py-1 rounded bg-slate-50/60 border border-slate-200">
            <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500 shrink-0">Ends</span>
            {[['left', 'Left', !!line.stop_end_left], ['right', 'Right', !!line.stop_end_right]].map(([side, lbl, isStop]) => (
              <label key={side} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {lbl}
                <select
                  data-testid={`comp-line-end-${side}-${ref_id}`}
                  value={isStop ? 'stop' : 'mitre'}
                  title={`${lbl} end — Mitre (joins another run) or Stop End (open end). Every run has 2 ends.`}
                  onChange={(e) => {
                    const l = side === 'left' ? e.target.value === 'stop' : !!line.stop_end_left;
                    const r = side === 'right' ? e.target.value === 'stop' : !!line.stop_end_right;
                    onUpdate({ stop_end_left: l, stop_end_right: r, mitre_count: 2 - (l ? 1 : 0) - (r ? 1 : 0), notes: withMitreTag(line.notes, false) });
                  }}
                  className={`text-[10px] px-1.5 py-0.5 border rounded bg-white normal-case ${isStop ? 'border-indigo-300 text-indigo-900' : 'border-cyan-300 text-cyan-900'}`}
                >
                  <option value="mitre">Mitre</option>
                  <option value="stop">Stop End</option>
                </select>
              </label>
            ))}
            <span data-testid={`comp-line-mitres-${ref_id}`} className="text-[10px] text-slate-500">
              ◢ {2 - (line.stop_end_left ? 1 : 0) - (line.stop_end_right ? 1 : 0)} mitre · {(line.stop_end_left ? 1 : 0) + (line.stop_end_right ? 1 : 0)} stop end
            </span>

            <span className="h-4 w-px bg-slate-300" aria-hidden="true" />

            <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500 shrink-0">Outlet</span>
            <select
              data-testid={`comp-line-outlet-size-${ref_id}`}
              value={line.outlet_size || ''}
              onChange={(e) => onUpdate({
                outlet_size: e.target.value,
                outlet_qty: e.target.value && !Number(line.outlet_qty) ? 1 : Number(line.outlet_qty) || 0,
              })}
              title="Outlet size — each outlet requires a matching dropper. Leave blank if this gutter run has no outlets."
              className="text-[10px] px-1.5 py-0.5 border border-slate-300 rounded bg-white"
            >
              <option value="">—</option>
              <option value="80mm">80mm round</option>
              <option value="65mm">65mm round</option>
              <option value="rect">Rect</option>
            </select>
            <input
              type="number"
              step="1"
              min="0"
              max="20"
              data-testid={`comp-line-outlet-qty-${ref_id}`}
              value={line.outlet_qty ?? 0}
              onChange={(e) => onUpdate({ outlet_qty: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              disabled={!line.outlet_size}
              title="Number of outlets on this gutter run."
              className="w-12 text-[10px] px-1 py-0.5 border border-slate-300 rounded font-mono-num text-right disabled:bg-slate-50 disabled:text-slate-400"
            />
            <span className="text-[9px] text-slate-400 -ml-1">qty</span>

            {Number(line.mitre_count) > 0 && (
              <>
                <span className="h-4 w-px bg-slate-300" aria-hidden="true" />
                <span data-testid={`comp-line-new-length-${ref_id}`}
                     className="inline-flex items-center gap-1 text-[10px] font-mono-num bg-cyan-50 text-cyan-900 border border-cyan-200 rounded px-1.5 py-0.5">
                  New length:
                  <strong>{t.line_total.toFixed(3)}m</strong>
                  <span className="text-cyan-600">
                    ({t.base_length.toFixed(2)} + {Number(line.mitre_count)} × {Math.round(mitreExtraM * 1000)}mm = +{t.mitre_extra_length.toFixed(3)})
                  </span>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Fastening details strip */}
      {sectionKey === 'Fastening' && (() => {
        const source = line.m2_source || 'roof_details';
        const facePinMax = facePinLengths.length ? facePinLengths[0].length_m : 0;
        const facePinSum = facePinLengths.reduce((a, p) => a + Number(p.length_m || 0), 0);
        const sourceVal = source === 'face_pin' ? facePinMax
                        : source === 'face_pin_sum' ? facePinSum
                        : Number(roofM2) || 0;
        const screwRate = Number(line.screw_rate) || 7;
        const totalM2 = Number(line.length) || 0;
        const screwQty = Math.ceil(totalM2 * screwRate);
        const drift = sourceVal > 0 && Math.abs(totalM2 - sourceVal) > 0.005;
        const applySource = (newSource) => {
          const nextVal = newSource === 'face_pin' ? facePinMax
                        : newSource === 'face_pin_sum' ? facePinSum
                        : Number(roofM2) || 0;
          const patch = { m2_source: newSource };
          if (nextVal > 0) patch.length = nextVal;
          onUpdate(patch);
        };
        return (
          <div className="col-span-12" data-testid={`comp-line-fastening-details-${ref_id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 pr-2 py-1 rounded bg-rose-50/40 border border-rose-100">
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-rose-700 shrink-0">m² source</span>
              <select
                data-testid={`comp-line-fastening-m2-source-${ref_id}`}
                value={source}
                onChange={(e) => applySource(e.target.value)}
                title="Pick where the Units value comes from. Roof Details uses the inspector's roof_m2. Face Pin uses the longest Face annotation on the diagram. Sum of Face Pins adds every Face annotation length together."
                className="text-[10px] px-1.5 py-0.5 border border-rose-200 rounded bg-white text-slate-700"
              >
                <option value="roof_details">Roof Details ({(Number(roofM2) || 0).toFixed(2)}m²)</option>
                {facePinLengths.length > 0 && (
                  <option value="face_pin">Face Pin ({facePinMax.toFixed(2)}m²)</option>
                )}
                {facePinLengths.length > 1 && (
                  <option value="face_pin_sum">Sum of Face Pins ({facePinSum.toFixed(2)}m²)</option>
                )}
              </select>

              {drift && (
                <button
                  type="button"
                  data-testid={`comp-line-fastening-reset-${ref_id}`}
                  onClick={() => onUpdate({ length: sourceVal })}
                  title={`Units is currently ${totalM2.toFixed(2)}m² but the picked source is ${sourceVal.toFixed(2)}m². Click to reset.`}
                  className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200"
                >
                  ↻ Reset to {sourceVal.toFixed(2)}m²
                </button>
              )}

              <span className="h-4 w-px bg-rose-200" aria-hidden="true" />

              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-rose-700 shrink-0">Screw Rate</span>
              <select
                data-testid={`comp-line-fastening-screw-rate-${ref_id}`}
                value={screwRate}
                onChange={(e) => onUpdate({ screw_rate: parseInt(e.target.value, 10) || 7 })}
                title="Screws per m². Default is 7 — most Iron reroofs use 7 screws/m² with Timberteks. Bump to 8 for steep pitches or down to 6 for low-pitch sheets."
                className="text-[10px] px-1.5 py-0.5 border border-rose-200 rounded bg-white text-slate-700 font-mono-num"
              >
                <option value={6}>6 / m²</option>
                <option value={7}>7 / m² (default)</option>
                <option value={8}>8 / m²</option>
              </select>

              <span
                data-testid={`comp-line-fastening-screw-qty-${ref_id}`}
                title={`${totalM2.toFixed(2)}m² × ${screwRate} screws/m² = ${screwQty} screws to order`}
                className="inline-flex items-center gap-1 text-[10px] font-mono-num bg-rose-100 text-rose-900 border border-rose-200 rounded px-1.5 py-0.5"
              >
                Order: <strong>{screwQty.toLocaleString()}</strong> screws
                <span className="text-rose-600">
                  ({totalM2.toFixed(2)} × {screwRate})
                </span>
              </span>

              {/* Row-scoped Tile Rescrew position picker (2026-02c) —
                  only rendered on tile-rescrew quotes; writing a value
                  triggers `useTileRescrewRowCascade` at parent level so
                  the correct fixings rate row cascades onto the line. */}
              {/rescrew.*(colortile|colourtile|decramastic|tile)|(colortile|colourtile|decramastic|tile).*rescrew/i.test(quoteTypeCtx || '') && (
                <FasteningPositionPicker
                  value={line.row_position || ''}
                  // Clear type_name AND numeric rate columns so switching
                  // Bottom → Ridge → Barge refreshes every field from
                  // the new rate card (otherwise Bottom's buy_cost /
                  // labour_rate would stick when switching to Ridge).
                  onChange={(pos) => onUpdate({
                    row_position: pos,
                    type_name: '',
                    buy_cost: 0,
                    materials_rate: 0,
                    labour_rate: 0,
                    contractor_rate: 0,
                    supplier: '',
                    supplier_code: '',
                    materials_desc: '',
                    labour_details: '',
                  })}
                  testidRef={ref_id}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* Iter254 — Recoat details strip. Shows the auto-filled area
          math (`📐 Recoat area: 322.49m² × 1 = 322.49m²`) and offers
          a "↻ Re-apply roof m²" button when the operator wants to
          re-sync after a roof_m2 edit. Recoat rates ending in (/m2)
          trigger the auto-fill on type pick; this strip surfaces the
          state and re-apply control. */}
      {sectionKey === 'ReCoat' && /\(\s*\/?m2\s*\)/i.test(line.type_name || '') && (() => {
        const m2 = Number(roofM2) || 0;
        const units = Number(line.length) || 0;
        const qty = Number(line.quantity) || 1;
        const drift = m2 > 0 && units > 0 && Math.abs(units - m2) > 0.005;
        const autoFilled = !!line.recoat_auto_filled;
        return (
          <div className="col-span-12" data-testid={`comp-line-recoat-details-${ref_id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 pr-2 py-1 rounded bg-red-50 border border-red-100">
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-red-700 shrink-0">
                Recoat area
              </span>
              <span className="text-[10px] font-mono-num text-slate-700">
                📐 <strong>{units.toFixed(2)}m²</strong> × {qty} = <strong className="text-red-700">{(units * qty).toFixed(2)}m²</strong>
                {m2 > 0 && <span className="text-slate-400 ml-1">· roof: {m2.toFixed(2)}m²</span>}
              </span>
              {autoFilled && !drift && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
                  ✓ auto
                </span>
              )}
              {drift && m2 > 0 && (
                <>
                  <span
                    data-testid={`comp-line-recoat-drift-${ref_id}`}
                    className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
                    title={`Units (${units.toFixed(2)}) differs from current roof_m2 (${m2.toFixed(2)}).`}
                  >
                    ⚠ drift
                  </span>
                  <button
                    type="button"
                    data-testid={`comp-line-recoat-reapply-${ref_id}`}
                    onClick={() => onUpdate({
                      length: m2,
                      quantity: 1,
                      recoat_auto_filled: true,
                      recoat_auto_source_m2: m2,
                    })}
                    className="text-[10px] underline text-slate-600 hover:text-slate-900"
                  >
                    ↻ Re-apply roof m² ({m2.toFixed(2)})
                  </button>
                </>
              )}
              {m2 <= 0 && (
                <span className="text-[10px] text-amber-600">
                  ⚠ No roof_m² set in Roof Details — Recoat area can't auto-fill.
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Iter255 — Safety / Scaffolding row strip. Scope pill toggle
          (Roofing/Gutter/Both), Estimation vs Confirmed pricing
          two-stage fields, "EST" badge while supplier hasn't responded,
          and the Request-quote button that downloads the Scaffold
          Quote Request PDF for the operator to email manually. */}
      {sectionKey === 'Safety' && (() => {
        const scope = (line.safety_scope || 'roofing').toLowerCase();
        const isEst = !!line.is_estimation;
        const hasConfirmed = (line.confirmed_supply_cost != null && line.confirmed_supply_cost !== '');
        const showEstBadge = isEst && !hasConfirmed;
        const fmt = (n) => (n == null || n === '' || Number.isNaN(Number(n))) ? '—' : `$${Number(n).toFixed(2)}`;
        // Compute customer-charge total for either stage so the
        // operator sees the math live: supply * (1 + markup/100).
        const totalOf = (supply, pct) => {
          const s = Number(supply); const p = Number(pct);
          if (!s) return null;
          return s * (1 + (Number.isFinite(p) ? p : 0) / 100);
        };
        const estTotal = totalOf(line.estimation_supply_cost, line.estimation_markup_pct);
        const confTotal = totalOf(line.confirmed_supply_cost, line.confirmed_markup_pct);
        // Picks confirmed when present, else estimation. Writes the
        // canonical buy_cost + materials_rate so existing customer-
        // charge math (qty × materials_rate − buy_cost = profit) is
        // unchanged. labour_rate=0 — Safety has no labour.
        const recompute = (patch) => {
          const next = { ...line, ...patch };
          const supply = next.confirmed_supply_cost != null && next.confirmed_supply_cost !== ''
                       ? Number(next.confirmed_supply_cost)
                       : Number(next.estimation_supply_cost || 0);
          const pct    = next.confirmed_supply_cost != null && next.confirmed_supply_cost !== ''
                       ? Number(next.confirmed_markup_pct || 0)
                       : Number(next.estimation_markup_pct || 0);
          const charge = supply * (1 + pct / 100);
          const hasConf2 = next.confirmed_supply_cost != null && next.confirmed_supply_cost !== '';
          onUpdate({
            ...patch,
            buy_cost: supply,
            materials_rate: charge,
            labour_rate: 0,
            is_estimation: !hasConf2,
            ...(hasConf2 && !line.quote_confirmed_at ? { quote_confirmed_at: new Date().toISOString() } : {}),
          });
        };
        const reqQuote = async () => {
          if (!line._id) { window.alert('Save the row first.'); return; }
          try {
            const resp = await api.post(
              `/appraisals/${line.appraisal_id}/safety/${line._id}/quote-request-pdf`,
              {}, { responseType: 'blob' },
            );
            window.open(URL.createObjectURL(resp.data), '_blank');
            onUpdate({ quote_requested_at: new Date().toISOString() });
          } catch (e) {
            window.alert('PDF failed: ' + (e?.response?.data?.detail || e.message));
          }
        };
        const numIn = (field, val, ph, w='w-20') => (
          <input
            type="number" step="0.01"
            data-testid={`comp-line-safety-${field}-${ref_id}`}
            value={val ?? ''}
            placeholder={ph}
            onChange={e => recompute({ [field]: e.target.value === '' ? null : Number(e.target.value) })}
            className={`${w} text-[11px] px-1.5 py-0.5 border border-slate-300 rounded font-mono-num`}
          />
        );
        return (
          <div className="col-span-12" data-testid={`comp-line-safety-details-${ref_id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-1 pr-2 py-1.5 rounded bg-amber-50 border border-amber-200">
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-amber-800 shrink-0">Safety scope</span>
              <div className="inline-flex rounded-full overflow-hidden text-[10px] font-bold uppercase tracking-wider border border-amber-300" role="group" aria-label="Safety scope">
                {['roofing','gutter','both'].map(opt => (
                  <button key={opt} type="button"
                    data-testid={`comp-line-safety-scope-${opt}-${ref_id}`}
                    onClick={() => onUpdate({ safety_scope: opt })}
                    className={`px-2 py-0.5 transition-colors ${scope===opt?'bg-amber-600 text-white':'bg-white text-amber-700 hover:bg-amber-100'}`}>
                    {opt}
                  </button>
                ))}
              </div>
              {showEstBadge && (
                <span data-testid={`comp-line-safety-est-badge-${ref_id}`}
                  title="Estimation — pending supplier confirmation."
                  className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-900 bg-amber-200 border border-amber-300 rounded px-1.5 py-0.5">⚠ EST</span>
              )}
              {hasConfirmed && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">✓ confirmed</span>
              )}
              <span className="ml-auto" />
              <button type="button"
                data-testid={`comp-line-safety-request-quote-${ref_id}`}
                onClick={reqQuote}
                className="text-[10px] font-bold uppercase tracking-wider bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded"
                title="Generate a Scaffold Quote Request PDF.">📄 Request quote</button>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 pr-2 py-1 mt-1 text-[10px] text-slate-600">
              <span className="font-bold uppercase tracking-wider text-slate-500 w-20 shrink-0">Estimation</span>
              <span className="text-slate-400">supply $</span>
              {numIn('estimation_supply_cost', line.estimation_supply_cost, '1800')}
              <span className="text-slate-400">+ markup</span>
              {numIn('estimation_markup_pct', line.estimation_markup_pct, '20', 'w-16')}
              <span className="text-slate-400">%</span>
              <span className="ml-1">= <strong className="text-amber-700">{fmt(estTotal)}</strong></span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 pr-2 py-1 text-[10px] text-slate-600">
              <span className="font-bold uppercase tracking-wider text-emerald-700 w-20 shrink-0">Confirmed</span>
              <span className="text-slate-400">supply $</span>
              {numIn('confirmed_supply_cost', line.confirmed_supply_cost, '1800')}
              <span className="text-slate-400">+ markup</span>
              {numIn('confirmed_markup_pct', line.confirmed_markup_pct, '20', 'w-16')}
              <span className="text-slate-400">%</span>
              <span className="ml-1">= <strong className="text-emerald-700">{fmt(confTotal)}</strong></span>
              <span className="text-slate-400 ml-2">· {line.scaffold_supplier_name || 'Pro Platform Limited'}</span>
              {line.quote_requested_at && (
                <span className="text-slate-400 ml-1" title={line.quote_requested_at}>
                  · requested {new Date(line.quote_requested_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Iter253 — Underlay details strip. Shows the roll-coverage math
          for this rate (e.g. 322.49m² ÷ 50m²/roll = 6.45 → 7 rolls
          ordered), surfaces drift when the operator manually overrides
          qty, and offers a Reset that re-applies the ceil computation.

          Iter257 — Adds a per-row "Full roof / Replacement only" mode
          toggle. In "Replacement only" mode the qty is derived from a
          user-picked subset of Sheet rows (their summed m²) instead
          of the full roof_m². Multiple Underlay rows on the same
          appraisal can independently sit in either mode — handy when
          one underlay type covers the whole roof and another covers
          a different zone (e.g. flat roof + main pitched roof). */}
      {sectionKey === 'Underlay' && (() => {
        const rateRow = (rates || []).find(r => r.type_name === line.type_name);
        const coverage = Number(rateRow?.roll_coverage_m2) || 0;
        const fullM2 = Number(roofM2) || 0;
        const mode = (line.underlay_mode || 'full');
        const selectedKeys = Array.isArray(line.replacement_sheet_keys) ? line.replacement_sheet_keys : [];
        // Sheet row m² formula = same one rendered on each Sheet row's
        // "Roof N m²" metric (qty × length when length>0, else qty,
        // multiplied by cover_width). cover_width is already in metres.
        const sheetM2 = (s) => {
          const cw = Number(s.cover_width) || 0;
          if (cw <= 0) return 0;
          const qty = Number(s.quantity) || 0;
          const len = Number(s.length) || 0;
          const lineal = len > 0 ? qty * len : qty;
          return lineal * cw;
        };
        const sheetEntries = (sheetLines || []).map((s, idx) => ({
          key: s._key,
          ref: `Sh${idx + 1}`,
          type_name: s.type_name || '(blank Sheet row)',
          m2: sheetM2(s),
        })).filter(e => e.m2 > 0);
        const selectedEntries = sheetEntries.filter(e => selectedKeys.includes(e.key));
        const selectedM2 = selectedEntries.reduce((a, e) => a + e.m2, 0);
        const effectiveM2 = mode === 'replacement' ? selectedM2 : fullM2;
        const computedQty = (coverage > 0 && effectiveM2 > 0) ? Math.ceil(effectiveM2 / coverage) : 0;
        const currentQty = Number(line.quantity) || 0;
        const drift = computedQty > 0 && currentQty !== computedQty;
        const autoFilled = !!line.qty_auto_filled;
        const exactRolls = (coverage > 0 && effectiveM2 > 0) ? (effectiveM2 / coverage) : 0;

        const setMode = (next) => {
          if (next === mode) return;
          const patch = { underlay_mode: next };
          if (next === 'replacement') {
            // Iter258b — Once-only auto-select on first mode switch.
            // The operator's intent on flipping to Replacement is "I'm
            // replacing roughly all the sheets in scope" — leaking m²=0
            // until they hand-pick chips is confusing, especially when
            // the Sheet section's running total is right there
            // (25.54 m² in the user's A5051 walkthrough). So we auto-
            // populate `replacement_sheet_keys` with every Sheet row
            // that has m² > 0, recompute qty from the full sum, and
            // hand off — the operator deselects chips for any sheets
            // they're NOT replacing. From then on the selection is
            // theirs to own (re-entering Replacement reuses whatever
            // they last picked).
            const prevKeys = Array.isArray(line.replacement_sheet_keys) ? line.replacement_sheet_keys : [];
            const startKeys = prevKeys.length > 0
              ? prevKeys
              : sheetEntries.map(e => e.key);
            const startEntries = sheetEntries.filter(e => startKeys.includes(e.key));
            const startM2 = startEntries.reduce((a, e) => a + e.m2, 0);
            const startQty = (coverage > 0 && startM2 > 0) ? Math.ceil(startM2 / coverage) : 0;
            patch.replacement_sheet_keys = startKeys;
            patch.quantity = startQty;
            patch.qty_auto_filled = startQty > 0;
            patch.qty_auto_source_m2 = startM2;
          } else {
            // Back to full-roof mode. Recompute qty from roof_m2.
            const newQty = (coverage > 0 && fullM2 > 0) ? Math.ceil(fullM2 / coverage) : 0;
            patch.quantity = newQty;
            patch.qty_auto_filled = newQty > 0;
            patch.qty_auto_source_m2 = fullM2;
          }
          onUpdate(patch);
        };

        const toggleSheet = (sheetKey) => {
          const nextKeys = selectedKeys.includes(sheetKey)
            ? selectedKeys.filter(k => k !== sheetKey)
            : [...selectedKeys, sheetKey];
          const nextEntries = sheetEntries.filter(e => nextKeys.includes(e.key));
          const nextM2 = nextEntries.reduce((a, e) => a + e.m2, 0);
          const nextQty = (coverage > 0 && nextM2 > 0) ? Math.ceil(nextM2 / coverage) : 0;
          onUpdate({
            replacement_sheet_keys: nextKeys,
            quantity: nextQty,
            qty_auto_filled: nextQty > 0,
            qty_auto_source_m2: nextM2,
          });
        };

        return (
          <div className="col-span-12" data-testid={`comp-line-underlay-details-${ref_id}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 pr-2 py-1 rounded bg-slate-50 border border-slate-200">
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-600 shrink-0">
                Mode
              </span>
              <div className="inline-flex rounded-md ring-1 ring-slate-300 overflow-hidden" data-testid={`comp-line-underlay-mode-${ref_id}`}>
                <button
                  type="button"
                  data-testid={`comp-line-underlay-mode-full-${ref_id}`}
                  onClick={() => setMode('full')}
                  title="Calculate underlay from the full roof m² (re-roof default)."
                  className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                    mode === 'full' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >Full roof</button>
                <button
                  type="button"
                  data-testid={`comp-line-underlay-mode-replacement-${ref_id}`}
                  onClick={() => setMode('replacement')}
                  title="Calculate underlay only for the m² of selected Sheet rows being replaced."
                  className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors border-l border-slate-300 ${
                    mode === 'replacement' ? 'bg-amber-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >Replacement only</button>
              </div>
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-600 shrink-0 ml-2">
                Rolls calc
              </span>
              {coverage > 0 ? (
                <span className="text-[10px] font-mono-num text-slate-700">
                  📐 <strong>{effectiveM2.toFixed(2)}m²</strong> ÷ {coverage}m²/roll = <strong>{exactRolls.toFixed(2)}</strong> → <strong className="text-emerald-700">{computedQty} rolls</strong>
                  {mode === 'replacement' && (
                    <span className="ml-1 text-amber-700 text-[9px]">(replacement scope)</span>
                  )}
                </span>
              ) : (
                <span className="text-[10px] text-slate-500">
                  Pick an Underlay rate with a roll-coverage spec (e.g. <em>Covertek 215 SS 50m²</em>) so the system can auto-compute rolls.
                </span>
              )}
              {autoFilled && !drift && coverage > 0 && (
                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
                  ✓ auto
                </span>
              )}
              {drift && coverage > 0 && (
                <>
                  <span
                    data-testid={`comp-line-underlay-drift-${ref_id}`}
                    className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
                    title={`Quantity (${currentQty}) differs from the auto-computed value (${computedQty}). Click Reset to re-apply.`}
                  >
                    ⚠ qty overridden
                  </span>
                  <button
                    type="button"
                    data-testid={`comp-line-underlay-reset-${ref_id}`}
                    onClick={() => onUpdate({
                      quantity: computedQty,
                      qty_auto_filled: true,
                      qty_auto_source_m2: effectiveM2,
                    })}
                    className="text-[10px] underline text-slate-600 hover:text-slate-900"
                  >
                    ↻ Reset to {computedQty}
                  </button>
                </>
              )}
            </div>

            {/* Iter257 — Sheet picker chips. Only visible in
                replacement mode; shows every Sheet row with positive
                m² as a tickable chip. Selected chips contribute to
                `selectedM2` shown above. */}
            {mode === 'replacement' && (
              <div
                data-testid={`comp-line-underlay-sheet-picker-${ref_id}`}
                className="mt-2 p-2 bg-amber-50/60 ring-1 ring-amber-200 rounded space-y-1"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-amber-900">
                    Sheets being replaced
                  </span>
                  {selectedEntries.length > 0 && (
                    <span className="text-[10px] font-mono-num text-amber-900">
                      Σ {selectedM2.toFixed(2)} m² · {selectedEntries.length} of {sheetEntries.length}
                    </span>
                  )}
                </div>
                {sheetEntries.length === 0 ? (
                  <div className="text-[10px] text-amber-800 italic">
                    No Sheet rows with m² to pick from. Add Sheet lines (with qty + cover_width) first.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {sheetEntries.map(e => {
                      const picked = selectedKeys.includes(e.key);
                      return (
                        <button
                          key={e.key}
                          type="button"
                          data-testid={`comp-line-underlay-sheet-${e.ref}-${ref_id}`}
                          onClick={() => toggleSheet(e.key)}
                          title={`${e.type_name} — ${e.m2.toFixed(2)} m²`}
                          className={`inline-flex items-center gap-1 text-[10px] font-mono-num px-1.5 py-0.5 rounded-full transition-colors ring-1 ${
                            picked
                              ? 'bg-amber-600 text-white ring-amber-600'
                              : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <span className="font-bold">{e.ref}</span>
                          <span>{e.m2.toFixed(2)} m²</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}


      {/* Flashings / Valleys template-driven workflow.
          Iter245 — Valley rows now show the same green template+folds
          panel that Flashing rows do. Cat is hard-wired to 'Valley'
          (no per-line flashing_type — every valley uses a Valley
          library template). Rate-snap uses the catalogue's
          "New {Material} Valley (400G/5F)" naming pattern. */}
      {((sectionKey === 'Flashing' && line.flashing_type) || sectionKey === 'Valley' || usesBargeCascade) && (() => {
        const cat = (sectionKey === 'Valley' || sectionKey === 'Barge') ? sectionKey : line.flashing_type;
        const rp = String(roofProfileCtx || '').trim().toLowerCase();
        const eligible = (flashingTemplates || []).filter(tpl => {
          if (tpl.category !== cat) return false;
          if (tpl.applies_to_all_profiles) return true;
          const tags = Array.isArray(tpl.sheet_profiles) ? tpl.sheet_profiles : [];
          if (!tags.length) return true;
          if (!rp) return true;
          return tags.some(s => String(s || '').trim().toLowerCase() === rp);
        });
        const picked = eligible.find(tpl => {
          const tid = String(tpl._id);
          return tid === String(line.flashing_template_id || '')
              || tid === String(line.linked_flashing_template_id || '');
        });
        const tplFoldsArr = picked ? (picked.folds || []) : [];
        const tplGirth = tplFoldsArr.reduce((a, f) => a + (Number(f.length_mm) || 0), 0);
        const lineFolds = Array.isArray(line.folds) ? line.folds : [];
        const girth = lineFolds.reduce((a, f) => a + (Number(f?.length_mm) || 0), 0);
        const folds = lineFolds.length;
        // Iter246 — "Folds" semantic = BENDS BETWEEN SEGMENTS, not the
        // length-segment count. A flashing with 3 length segments has
        // 2 bends, hence 2F. Applies universally — the rate catalogue
        // was renamed from `100W/3F → 100W/2F` to match this rule.
        // Templates named "5Fold Valley" / "3Fold Valley" still use
        // their declared count as the source of truth when present.
        const rateFolds = (() => {
          const m = String(picked?.name || '').toLowerCase().match(/(\d+)\s*fold/);
          if (m) return parseInt(m[1], 10);
          return Math.max(0, folds - 1);
        })();
        const girthDrift = picked && girth !== tplGirth;
        const mat = String(line.material || '').toLowerCase();
        const rateMatch = (girth > 0 && rateFolds > 0 && mat)
          ? (() => {
              const cands = (rates || [])
                .map(r => {
                  const tn = String(r.type_name || '').toLowerCase();
                  if (!tn.includes(mat) || !tn.includes(`${rateFolds}f`)) return null;
                  // Iter245 — Flashing "100W/4F" OR Valley "(400G/5F)".
                  let m = tn.match(/(\d+)w[/\s)]/);
                  if (!m) m = tn.match(/(\d+)g\//);
                  return m ? { row: r, width: parseInt(m[1], 10) } : null;
                })
                .filter(Boolean)
                .filter(c => c.width >= girth)
                .sort((a, b) => a.width - b.width);
              return cands.length ? cands[0].row : null;
            })()
          : null;
        const rateSnapped = rateMatch && (() => {
          const tn = String(rateMatch.type_name).toLowerCase();
          const m = tn.match(/(\d+)w[/\s)]/) || tn.match(/(\d+)g\//);
          return m && parseInt(m[1], 10) !== girth;
        })();

        const applyTemplate = async (tpl) => {
          if (!tpl) {
            // Iter245 — clear both legacy + linked fields so Valley AND
            // Flashing rows stay internally consistent.
            onUpdate({
              flashing_template_id: '',
              linked_flashing_template_id: '',
              linked_flashing_name: '',
              girth_mm: 0, folds_qty: 0, folds: [],
            });
            return;
          }
          const copy = (tpl.folds || []).map(f => ({
            length_mm: Number(f.length_mm) || 0,
            bend_angle: Number(f.bend_angle ?? 90),
            bend_dir: f.bend_dir === 'ccw' ? 'ccw' : 'cw',
          }));
          const g = copy.reduce((a, f) => a + (Number(f.length_mm) || 0), 0);
          // Iter247 — write the fold signature into `profile` so the
          // Components Profile dropdown stays in sync after a template
          // pick on Valley rows. Harmless for Flashing rows (their
          // profile dropdown ignores Valley signatures).
          const _sig = copy.map(f => Math.round(Number(f.length_mm) || 0)).join('/');
          // folds_qty = BENDS (template "N fold" wins, else segments − 1) — matches the rate "…W/2F" naming.
          const tplFoldMatch = String(tpl.name || '').toLowerCase().match(/(\d+)\s*fold/);
          const bends = tplFoldMatch ? parseInt(tplFoldMatch[1], 10) : Math.max(0, copy.length - 1);

          // Iter248/250 — Valley + Barge (iron) rows: snap Type to the
          // matching rate row name (e.g. "New Galv Valley (400G/5F)"
          // or "Corru Barge Galv 300G/4F") rather than the template
          // name. Flashing rows keep the legacy template-name placeholder.
          let effMaterial = line.material || '';
          let matchedRate = null;
          if (usesFlashingCascade) {
            if (!effMaterial && primaryMaterialCtx) {
              // eslint-disable-next-line no-alert
              const ok = window.confirm(
                `This ${sectionKey} row has no Material set.\n\n` +
                `Default to "${primaryMaterialCtx}" (the appraisal's primary material) so the Type can auto-resolve?`
              );
              if (ok) effMaterial = primaryMaterialCtx;
            }
            matchedRate = resolveRateByGirthFolds(effMaterial, g, bends);
            // Iter249/250 — out-of-catalogue fallback: mint synthetic
            // rate by composing material cost from the Flashing
            // catalogue + labour/contractor from existing Valley/Barge rows.
            if (!matchedRate && effMaterial && g > 0 && bends > 0 && g <= VALLEY_COIL_MAX_MM) {
              const sheetProfile = usesBargeCascade ? inferBargeSheetProfile(tpl, null) : null;
              matchedRate = await mintSyntheticValleyRate(effMaterial, g, bends, { sheetProfile });
            }
          }

          const patch = {
            flashing_template_id: String(tpl._id),
            // Iter245 — keep the diagram-sourced `linked_*` fields in
            // sync so the cascade contract holds whether the operator
            // picked here or in the annotator's pin dialog.
            linked_flashing_template_id: String(tpl._id),
            linked_flashing_name: tpl.name || '',
            girth_mm: g,
            folds_qty: bends,
            folds: copy,
            start_angle_deg: Number(tpl.start_angle_deg) || 0,
            ...(usesFlashingCascade ? { profile: _sig } : {}),
          };
          if (usesFlashingCascade) {
            if (effMaterial && effMaterial !== line.material) patch.material = effMaterial;
            // Iter250 — Barge rows persist the inferred sheet_profile
            // so the row's identity survives template swaps.
            if (usesBargeCascade) {
              const sp = inferBargeSheetProfile(tpl, null);
              if (sp) patch.sheet_profile = sp;
            }
            if (matchedRate) {
              patch.type_name      = matchedRate.type_name;
              patch.buy_cost       = Number(matchedRate.buy_cost)       || 0;
              patch.materials_rate = Number(matchedRate.materials_rate) || 0;
              patch.labour_rate    = Number(matchedRate.labour_rate)    || 0;
              patch.contractor_rate= Number(matchedRate.contractor_rate)|| 0;
              patch.override       = false;
            } else {
              patch.type_name = '';
            }
          } else {
            // Flashing rows: keep the existing behaviour (template name
            // as the rate name placeholder; rate-snap upgrades it on
            // the next fold edit).
            patch.type_name = tpl.name;
          }
          onUpdate(patch);
        };

        // Iter224 — snap-up resolver factored out so the folds handlers
        // can auto-apply the next-width rate row when girth changes.
        // Returns the matched rate row OR null when nothing fits.
        // Iter245 — also matches the Valley catalogue's
        // "(400G/5F)" naming pattern in addition to the Flashing
        // "100W/4F" pattern.
        const resolveSnapUp = (g, fCount) => {
          if (!g || g <= 0 || !fCount || fCount <= 0 || !mat) return null;
          const cands = (rates || [])
            .map(r => {
              const tn = String(r.type_name || '').toLowerCase();
              if (!tn.includes(mat) || !tn.includes(`${fCount}f`)) return null;
              // Flashing pattern: "Galv 100W/4F"
              let m = tn.match(/(\d+)w[/\s)]/);
              // Valley pattern: "New Galv Valley (400G/5F)"
              if (!m) m = tn.match(/(\d+)g\//);
              return m ? { row: r, width: parseInt(m[1], 10) } : null;
            })
            .filter(Boolean)
            .filter(c => c.width >= g)
            .sort((a, b) => a.width - b.width);
          return cands.length ? cands[0].row : null;
        };

        // Iter224 — patch builder that auto-applies the snap-up Type
        // when the girth/folds combo resolves to a different rate row
        // than what's currently picked. Stashes the previous Type +
        // rates into `snapUndo` so the operator can revert with one
        // click. If snap-up resolves the SAME type_name (within-width
        // change) we leave the row alone.
        const withSnapPatch = (basePatch, nextGirth, nextFoldsCount) => {
          // Iter247 — for Valley rows, always keep `profile` in lock-
          // step with the current folds. The signature is the slash-
          // joined integer mm list, matching the format emitted by
          // the cascade and the /valley-profile-options endpoint.
          if (usesFlashingCascade && Array.isArray(basePatch.folds)) {
            const sig = basePatch.folds
              .map(f => Math.round(Number(f?.length_mm) || 0))
              .join('/');
            basePatch = { ...basePatch, profile: sig };
          }
          // Iter246 — effective rate fold count = bends. Template name
          // wins when it declares "NFold"; otherwise `segments - 1`.
          const nameFoldMatch = String(picked?.name || '').toLowerCase().match(/(\d+)\s*fold/);
          const effFolds = nameFoldMatch ? parseInt(nameFoldMatch[1], 10) : Math.max(0, nextFoldsCount - 1);
          const snapped = resolveSnapUp(nextGirth, effFolds);
          if (!snapped) return basePatch;
          const currentName = line.type_name || '';
          if (snapped.type_name === currentName) return basePatch;
          const currLc = currentName.toLowerCase();
          // Iter248 — relax the family guard for Valley rows. After a
          // Profile/Template pick the row may carry a template-style
          // type_name ("5Fold Valley") that doesn't contain the
          // material token — but we still want fold-edits to snap to
          // the rate-style name ("New Galv Valley (400G/5F)"). Detect
          // the template-name shape (contains "fold") and treat it as
          // a blank slate for snap purposes.
          const isTemplateLikeName = /fold/i.test(currentName) && !/\d+g\/|\d+w\//i.test(currentName);
          // Jun 2026 — any rate-style name of the same material is the same family, whatever its fold count,
          // so adding/removing a fold re-snaps 2F → 3F.
          const isRateStyleName = /\d+w\/\d+f|\d+g\/\d+f/i.test(currentName);
          const sameFamily = !currentName
            || isTemplateLikeName
            || (mat && currLc.includes(mat) && (isRateStyleName || currLc.includes(`${effFolds}f`)));
          if (!sameFamily) return basePatch;
          // Stash undo state — preserve the previous Type + 4 rate
          // numbers so we can roll back exactly.
          setSnapUndo({
            from_type_name: currentName,
            to_type_name:   snapped.type_name,
            prev_rates: {
              buy_cost:        line.buy_cost,
              materials_rate:  line.materials_rate,
              labour_rate:     line.labour_rate,
              contractor_rate: line.contractor_rate,
              supplier:        line.supplier,
              supplier_code:   line.supplier_code,
              materials_desc:  line.materials_desc,
              labour_details:  line.labour_details,
            },
          });
          return {
            ...basePatch,
            type_name:       snapped.type_name,
            buy_cost:        snapped.buy_cost        ?? line.buy_cost,
            materials_rate:  snapped.materials_rate  ?? line.materials_rate,
            labour_rate:     snapped.labour_rate     ?? line.labour_rate,
            contractor_rate: snapped.contractor_rate ?? line.contractor_rate,
            supplier:        snapped.supplier        ?? line.supplier,
            supplier_code:   snapped.supplier_code   ?? line.supplier_code,
            materials_desc:  snapped.materials_desc  ?? line.materials_desc,
            labour_details:  snapped.labour_details  ?? line.labour_details,
            override:        false,
          };
        };

        const undoSnap = () => {
          if (!snapUndo) return;
          onUpdate({
            type_name:       snapUndo.from_type_name,
            buy_cost:        snapUndo.prev_rates.buy_cost ?? 0,
            materials_rate:  snapUndo.prev_rates.materials_rate ?? 0,
            labour_rate:     snapUndo.prev_rates.labour_rate ?? 0,
            contractor_rate: snapUndo.prev_rates.contractor_rate ?? 0,
            supplier:        snapUndo.prev_rates.supplier || '',
            supplier_code:   snapUndo.prev_rates.supplier_code || '',
            materials_desc:  snapUndo.prev_rates.materials_desc || '',
            labour_details:  snapUndo.prev_rates.labour_details || '',
          });
          setSnapUndo(null);
        };

        const updateFold = async (idx, patch) => {
          const next = lineFolds.map((f, i) => i === idx ? { ...f, ...patch } : f);
          const g = next.reduce((a, f) => a + (Number(f?.length_mm) || 0), 0);
          await applyFoldPatch({ folds: next, girth_mm: g, folds_qty: Math.max(0, next.length - 1) }, g, next.length);
        };
        const addFold = async () => {
          const next = [...lineFolds, { length_mm: 50, bend_angle: 90, bend_dir: 'cw' }];
          const g = next.reduce((a, f) => a + (Number(f?.length_mm) || 0), 0);
          await applyFoldPatch({ folds: next, girth_mm: g, folds_qty: Math.max(0, next.length - 1) }, g, next.length);
        };
        const removeFold = async (idx) => {
          const next = lineFolds.filter((_, i) => i !== idx);
          const g = next.reduce((a, f) => a + (Number(f?.length_mm) || 0), 0);
          await applyFoldPatch({ folds: next, girth_mm: g, folds_qty: Math.max(0, next.length - 1) }, g, next.length);
        };

        // Iter249/250 — Wraps withSnapPatch with a synthetic-rate mint
        // fallback for Valley + Iron Barge rows whose girth exceeds the
        // catalogue bands. When the catalogue offers no matching rate,
        // we POST to /component-rates/{valley|barge}-synthetic to
        // compose one (material cost ← Flashing catalogue,
        // labour/contractor ← Valley/Barge reference row) and patch the
        // line with the new rate values + name.
        const applyFoldPatch = async (basePatch, nextGirth, nextFoldsCount) => {
          // First, run the standard snap-up resolver.
          const snapPatched = withSnapPatch(basePatch, nextGirth, nextFoldsCount);
          // For sections without the cascade UI, OR when snap-up
          // already fired, OR when material isn't set yet, just apply
          // and stop.
          if (!usesFlashingCascade) { onUpdate(snapPatched); return; }
          if (!mat) { onUpdate(snapPatched); return; }
          // Bends: prefer template-name-declared count; fallback to bend-edges.
          const nameFoldMatch = String(picked?.name || '').toLowerCase().match(/(\d+)\s*fold/);
          const effFolds = nameFoldMatch ? parseInt(nameFoldMatch[1], 10) : Math.max(0, nextFoldsCount - 1);
          // Did snap-up already write a new type_name? If yes, done.
          if (snapPatched.type_name && /\d+g\/\d+f\)?/i.test(snapPatched.type_name)) {
            onUpdate(snapPatched); return;
          }
          // Snap-up couldn't find a Valley/Barge rate. Check if the
          // row's current type_name still matches the live girth/bends —
          // if so, no further action needed.
          const stillMatches = (() => {
            const tn = String(line.type_name || '').toLowerCase();
            if (!tn) return false;
            const gm = tn.match(/(\d+)g\/(\d+)f/);
            if (!gm) return false;
            return parseInt(gm[1], 10) === nextGirth && parseInt(gm[2], 10) === effFolds;
          })();
          if (stillMatches) { onUpdate(snapPatched); return; }
          // Out-of-range or new dimension combo — mint synthetic.
          if (nextGirth <= 0 || nextGirth > VALLEY_COIL_MAX_MM) {
            // Clear the rate but keep the folds change.
            onUpdate({ ...snapPatched, type_name: '' });
            return;
          }
          const sheetProfile = usesBargeCascade ? inferBargeSheetProfile(picked, null) : null;
          const minted = await mintSyntheticValleyRate(line.material, nextGirth, effFolds, { sheetProfile });
          if (!minted) { onUpdate({ ...snapPatched, type_name: '' }); return; }
          onUpdate({
            ...snapPatched,
            type_name:       minted.type_name,
            buy_cost:        Number(minted.buy_cost)       || 0,
            materials_rate:  Number(minted.materials_rate) || 0,
            labour_rate:     Number(minted.labour_rate)    || 0,
            contractor_rate: Number(minted.contractor_rate)|| 0,
            override:        false,
          });
        };

        return (
          <div className="col-span-12" data-testid={`comp-line-flashing-details-${ref_id}`}>
            <div className="flex flex-wrap items-start gap-x-3 gap-y-1 pl-1 pr-2 py-1 rounded bg-emerald-50/40 border border-emerald-100">
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 shrink-0 pt-1">Template</span>

              <FlashingTemplatePickerInline
                ref_id={ref_id}
                eligible={eligible}
                picked={picked}
                cat={cat}
                roofProfileCtx={roofProfileCtx}
                onPick={applyTemplate}
              />

              <span className="h-4 w-px bg-emerald-200 mt-2" aria-hidden="true" />

              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 shrink-0 pt-1">Folds</span>
                {lineFolds.length === 0 && (
                  <span className="text-[10px] text-slate-400 italic pt-1">Pick a template to populate folds</span>
                )}
                {lineFolds.map((f, idx) => {
                  const tplFold = tplFoldsArr[idx];
                  const tplLen = Number(tplFold?.length_mm) || 0;
                  const drift = tplFold && Number(f.length_mm) !== tplLen;
                  return (
                    <div key={idx} className="inline-flex items-center bg-white border border-emerald-200 rounded pl-1">
                      <span className="text-[9px] font-bold text-emerald-700 mr-0.5">{idx + 1}</span>
                      <input
                        type="number" step="1" min="0" max="2000"
                        data-testid={`comp-line-flashing-fold-${idx}-${ref_id}`}
                        value={Number(f.length_mm) || 0}
                        onChange={(e) => updateFold(idx, { length_mm: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                        title={`Fold ${idx + 1} length (mm). Per-appraisal edit; template is not modified.`}
                        className="w-12 text-[11px] px-0.5 py-0.5 border-l border-emerald-100 font-mono-num text-right"
                      />
                      {drift && (
                        <button
                          type="button"
                          data-testid={`comp-line-flashing-fold-reset-${idx}-${ref_id}`}
                          onClick={() => updateFold(idx, { length_mm: tplLen })}
                          title={`Fold ${idx + 1} drifted from template (${tplLen}mm). Click to reset.`}
                          className="text-[9px] font-bold text-amber-800 hover:bg-amber-100 px-1"
                        >↻</button>
                      )}
                      <button
                        type="button"
                        data-testid={`comp-line-flashing-fold-remove-${idx}-${ref_id}`}
                        onClick={() => removeFold(idx)}
                        title={`Remove fold ${idx + 1}`}
                        className="text-[10px] text-rose-500 hover:bg-rose-50 px-1 rounded-r"
                      >×</button>
                    </div>
                  );
                })}
                {lineFolds.length > 0 && lineFolds.length < 20 && (
                  <button
                    type="button"
                    data-testid={`comp-line-flashing-fold-add-${ref_id}`}
                    onClick={addFold}
                    title="Add a fold (defaults to 50mm @ 90°)"
                    className="text-[10px] font-bold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-1.5 py-0.5 rounded border border-emerald-200"
                  >+ Fold</button>
                )}
                <span
                  data-testid={`comp-line-flashing-girth-total-${ref_id}`}
                  className={`ml-1 inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded font-mono-num border ${
                    usesFlashingCascade && girth > VALLEY_COIL_MAX_MM
                      ? 'bg-rose-100 text-rose-900 border-rose-200'
                      : 'bg-emerald-100 text-emerald-900 border-emerald-200'
                  }`}
                  title={usesFlashingCascade && girth > VALLEY_COIL_MAX_MM
                    ? `Girth ${girth}mm exceeds the ${VALLEY_COIL_MAX_MM}mm coil maximum — split this ${sectionKey.toLowerCase()} into two pieces.`
                    : null}
                >
                  Σ {girth}mm{usesFlashingCascade && girth > VALLEY_COIL_MAX_MM ? ` ⚠` : ''}
                </span>
                {girthDrift && (
                  <button
                    type="button"
                    data-testid={`comp-line-flashing-girth-reset-${ref_id}`}
                    onClick={() => applyTemplate(picked)}
                    title={`Total girth differs from template (${tplGirth}mm). Click to reset every fold + start angle.`}
                    className="text-[10px] font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 px-1.5 py-0.5 rounded border border-amber-200"
                  >↻ Reset all</button>
                )}
                {/* Iter247 — Switch-to-matching-template chip. Appears
                    when the live fold signature matches a DIFFERENT
                    catalogue template (not the currently-picked one).
                    Operator clicks to swap; no silent auto-swap. */}
                {usesFlashingCascade && (() => {
                  const liveSig = lineFolds
                    .map(f => Math.round(Number(f?.length_mm) || 0))
                    .join('/');
                  if (!liveSig) return null;
                  const match = (profileOptionsForSection || []).find(o => o.signature === liveSig);
                  if (!match) return null;
                  if (picked && String(match.template_id) === String(picked._id)) return null;
                  const matchTpl = eligible.find(t => String(t._id) === String(match.template_id));
                  if (!matchTpl) return null;
                  return (
                    <button
                      type="button"
                      data-testid={`comp-line-valley-switch-template-${ref_id}`}
                      onClick={() => applyTemplate(matchTpl)}
                      title={`Folds match ${match.template_name}. Click to swap the linked template.`}
                      className="text-[10px] font-bold text-sky-800 bg-sky-50 hover:bg-sky-100 px-1.5 py-0.5 rounded border border-sky-200"
                    >⇄ Switch to {match.template_name}</button>
                  );
                })()}
              </div>

              <span className="h-4 w-px bg-emerald-200 mt-2" aria-hidden="true" />

              {/* Iter249 — Synthetic-rate / coil-ceiling state machine:
                  · synthMinting=true → "⏳ Composing custom rate…"
                  · Valley row whose girth > 1200mm → "⚠ Exceeds 1200mm coil"
                  · effectiveActive.synthetic OR matching name on the
                    line that wasn't in `rates` yet → "💰 Custom valley
                    rate · sourced from {flashing_name}" */}
              {usesFlashingCascade && synthMinting ? (
                <span
                  data-testid={`comp-line-valley-rate-minting-${ref_id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-mono-num rounded px-1.5 py-0.5 border mt-1 bg-sky-100 text-sky-900 border-sky-200"
                >
                  ⏳ Composing custom rate…
                </span>
              ) : usesFlashingCascade && girth > VALLEY_COIL_MAX_MM ? (
                <span
                  data-testid={`comp-line-valley-rate-over-coil-${ref_id}`}
                  title={`A single flashing coil is ${VALLEY_COIL_MAX_MM}mm wide max. Split this ${sectionKey.toLowerCase()} into two pieces or reduce the girth.`}
                  className="inline-flex items-center gap-1 text-[10px] font-mono-num rounded px-1.5 py-0.5 border mt-1 bg-rose-100 text-rose-900 border-rose-200"
                >
                  ⚠ Exceeds {VALLEY_COIL_MAX_MM}mm coil — split this {sectionKey.toLowerCase()}
                </span>
              ) : (
                <span
                  data-testid={`comp-line-flashing-rate-hint-${ref_id}`}
                  title={rateMatch
                    ? `Rate resolved: ${rateMatch.type_name} → $${rateMatch.materials_rate}/m mat + $${rateMatch.labour_rate}/m lab`
                    : (girth > 0 && folds > 0 && !mat
                        ? 'Set Material above so the Type can auto-resolve.'
                        : (girth > 0 && folds > 0 && mat
                            ? `No rate row matched "${mat} ${girth}W/${rateFolds}F" — pick a Type from the dropdown manually.`
                            : 'Pick a template to derive Girth + Folds, then the rate row resolves automatically.'))}
                  className={`inline-flex items-center gap-1 text-[10px] font-mono-num rounded px-1.5 py-0.5 border mt-1 ${rateMatch
                      ? 'bg-emerald-100 text-emerald-900 border-emerald-200'
                      : (girth > 0 && folds > 0 ? 'bg-amber-100 text-amber-900 border-amber-200' : 'bg-slate-100 text-slate-500 border-slate-200')}`}
                >
                  {rateMatch
                    ? <>Rate: <strong>{rateMatch.type_name}</strong>{rateSnapped ? <span className="text-emerald-600 ml-1">(snap-up from {girth}mm)</span> : null}</>
                    : (girth > 0 && folds > 0 && !mat
                        ? <>⚠ Pick Material to resolve Type</>
                        : (girth > 0 && folds > 0 && mat
                            ? <>⚠ No rate for {mat} {girth}W/{rateFolds}F</>
                            : 'Pick a template to auto-resolve rate'))}
                </span>
              )}

              {/* Iter249 — Source attribution for synthetic Valley rates.
                  Lookup the active rate row in the freshly-refreshed
                  rates list; if it carries the `synthetic` flag, show
                  which Flashing rate sourced the buy_cost + materials_rate. */}
              {usesFlashingCascade && activeRateRow && activeRateRow.synthetic && activeRateRow.source_type_name && (
                <span
                  data-testid={`comp-line-valley-rate-synth-source-${ref_id}`}
                  title={`This ${sectionKey.toLowerCase()}'s girth (${activeRateRow.girth_mm}mm) is outside the catalogue bands. Materials cost was borrowed from the Flashing rate "${activeRateRow.source_type_name}". Labour & contractor rates use the standard ${sectionKey} pricing.`}
                  className="inline-flex items-center gap-1 text-[10px] font-mono-num rounded px-1.5 py-0.5 border mt-1 bg-violet-50 text-violet-800 border-violet-200"
                >
                  💰 Custom {sectionKey.toLowerCase()} · sourced from <strong className="ml-0.5">{activeRateRow.source_type_name}</strong>
                </span>
              )}

              {/* Iter251 — Profile + Profile Ref consistency warning.
                  When the operator manually types a custom Profile that
                  doesn't match the picked Profile Ref (or vice versa),
                  surface a small chip so the inconsistency isn't lost
                  silently downstream. */}
              {usesFlashingCascade && !profileRefConsistent && (
                <span
                  data-testid={`comp-line-profile-ref-mismatch-${ref_id}`}
                  title="Profile dimensions and Profile Ref name don't match a known template combination."
                  className="inline-flex items-center gap-1 text-[10px] font-mono-num rounded px-1.5 py-0.5 border mt-1 bg-amber-50 text-amber-800 border-amber-200"
                >
                  ⚠ Profile + Ref mismatch — pick a Ref or accept custom
                </span>
              )}

              {/* Iter224 — Snap-up Undo. When the folds editor auto-applied
                  a new Type (e.g. Galv 700/3F → Galv 800/3F because girth
                  jumped past the prior width band), surface a one-click
                  revert so the operator isn't surprised by a silent rate
                  swap. Disappears once they edit anything else. */}
              {snapUndo && (
                <button
                  type="button"
                  data-testid={`comp-line-flashing-snap-undo-${ref_id}`}
                  onClick={undoSnap}
                  title={`Snap-up auto-applied: ${snapUndo.from_type_name || '(blank)'} → ${snapUndo.to_type_name}. Click to revert.`}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border mt-1 bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 transition-colors"
                >
                  ↻ Snapped to {snapUndo.to_type_name} · undo
                </button>
              )}

              {/* Iter247 — Live cross-section preview for Valley rows.
                  Drawn from `line.folds` + `line.start_angle_deg`, so it
                  reflects mm-level edits the moment the operator types
                  them. Same renderer the annotator uses — the diagram
                  the supplier sees on the order PDF will match. */}
              {/* Iter252 — Show the live cross-section preview on every
                  section that surfaces the green Linked Flashing Template
                  panel (Valley, Iron Barge, AND Flashing). User noted
                  the Flashing template picker previously missed the
                  preview that Barge/Valley already get. */}
              {Array.isArray(line.folds) && line.folds.length > 0 && (
                <div
                  data-testid={`comp-line-valley-preview-${ref_id}`}
                  className="mt-2 pt-2 border-t border-emerald-200/60 flex flex-col items-start gap-1"
                >
                  <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                    Cross-Section · live
                  </div>
                  <div style={{ minWidth: 220, maxWidth: 320 }}>
                    <FoldCrossSectionPreview
                      folds={line.folds}
                      startAngleDeg={Number(line.start_angle_deg) || 0}
                    />
                  </div>
                </div>
              )}

              {/* Iter233 — Has Penetration? Dektite link cascade. Lets
                  the operator attach one (or many) Dektites to this
                  Flashing row; each link idempotently upserts a paired
                  Aquaseal/Dektite component_line via the parent's
                  onLinkDektite callback. */}
              <DektiteLinkInline
                api={api}
                ref_id={ref_id}
                roofType={roofTypeCtx}
                roofProfile={roofProfileCtx}
                linkedDektites={Array.isArray(line.linked_dektites) ? line.linked_dektites : []}
                onLinkDektite={onLinkDektite}
                onUnlinkDektite={onUnlinkDektite}
                onChangeDektite={onChangeDektite}
              />
            </div>
          </div>
        );
      })()}

      {/* Ridge / Barge cap calculator */}
      {line.component_type === 'Ridge' && (
        <CapPanel api={api} line={line} onUpdate={onUpdate} dropdownCategory="ridge_cap_types" label="Ridge caps" colour="purple" roofSubtypeCtx={roofSubtypeCtx} tileProfileCtx={tileProfileOverrideCtx} appraisalIdCtx={appraisalIdCtx} />
      )}
      {line.component_type === 'Barge' && (
        <CapPanel api={api} line={line} onUpdate={onUpdate} dropdownCategory="barge_cap_types" label="Barge caps" colour="violet" roofSubtypeCtx={roofSubtypeCtx} tileProfileCtx={tileProfileOverrideCtx} appraisalIdCtx={appraisalIdCtx} />
      )}

      {/* Iter234 — "Has Backtray?" cascade on Aquaseal/Dektite rows.
          Lets the operator link a Flashing row (Backtray sub-type
          preferred) to this Dektite. The parent handles bidirectional
          sync — picking an existing Flashing row also stamps the
          reciprocal Iter233 `linked_dektites` entry on that row. */}
      {sectionKey === 'Aquaseal' && (
        <DektiteCascadeInline
          api={api}
          ref_id={ref_id}
          line={line}
          onUpdate={onUpdate}
          roofTypeCtx={roofTypeCtx}
        />
      )}
      {sectionKey === 'Aquaseal' && (
        <BacktrayLinkInline
          ref_id={ref_id}
          dektiteDiaMm={Number(line.outlet_diameter_mm) || 0}
          roofProfileCtx={roofProfileCtx}
          linkedBacktray={line.linked_backtray || null}
          flashingLines={flashingLines}
          flashingTemplates={flashingTemplates}
          onLinkBacktray={onLinkBacktray}
          onUnlinkBacktray={onUnlinkBacktray}
          onChangeBacktray={onChangeBacktray}
        />
      )}

      {/* Rate-card details (auto-filled, expandable) */}
      <div className="col-span-12 -mt-1">
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 select-none flex items-center gap-1">
            Rate card · supplier · description
            {line.override && <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded ring-1 ring-amber-100"><AlertTriangle size={10} className="inline -mt-0.5" /> Overridden</span>}
            {isAdmin && <button type="button" onClick={(e) => { e.preventDefault(); setEditingRates(v => !v); }} className="ml-auto text-[10px] text-[#D35400] font-semibold flex items-center gap-1"><Edit2 size={10} /> {editingRates ? 'Lock rates' : 'Override rates'}</button>}
          </summary>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { k: 'buy_cost', l: 'Buy cost' },
              { k: 'materials_rate', l: 'Materials' },
              { k: 'labour_rate', l: 'Labour' },
              { k: 'contractor_rate', l: 'Contractor' },
            ].map(f => (
              <div key={f.k}>
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{f.l}</label>
                <input type="number" step="0.001" value={line[f.k] ?? 0} disabled={!editingRates} onChange={e => onUpdate({ [f.k]: parseFloat(e.target.value) || 0, override: true })} className="w-full text-xs px-2 py-1 border border-[#E2E2DF] rounded font-mono-num disabled:bg-slate-50 disabled:text-slate-500" />
              </div>
            ))}
            <div className="col-span-2 md:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Material description</label>
              <input value={line.materials_desc || ''} onChange={e => onUpdate({ materials_desc: e.target.value })} className="w-full text-xs px-2 py-1 border border-[#E2E2DF] rounded" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Supplier</label>
              <input value={line.supplier || ''} onChange={e => onUpdate({ supplier: e.target.value })} className="w-full text-xs px-2 py-1 border border-[#E2E2DF] rounded" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Supplier code</label>
              <input value={line.supplier_code || ''} onChange={e => onUpdate({ supplier_code: e.target.value })} className="w-full text-xs px-2 py-1 border border-[#E2E2DF] rounded" />
            </div>
            <div className="col-span-2 md:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Labour details</label>
              <input value={line.labour_details || ''} onChange={e => onUpdate({ labour_details: e.target.value })} className="w-full text-xs px-2 py-1 border border-[#E2E2DF] rounded" />
            </div>
            <div className="col-span-2 md:col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Notes</label>
              <input value={line.notes || ''} onChange={e => onUpdate({ notes: e.target.value })} className="w-full text-xs px-2 py-1 border border-[#E2E2DF] rounded" />
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
